import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IoCloseOutline,
  IoCodeSlashOutline,
  IoCloudUploadOutline,
  IoMenuOutline,
  IoShareOutline,
  IoArrowBack,
  IoDocumentOutline,
  IoDocumentsOutline,
  IoGlobeOutline,
  IoStatsChartOutline,
} from 'react-icons/io5';
import { PiCompassRose } from 'react-icons/pi';
import { MdOutlineTerrain } from 'react-icons/md';

import AtlasChartView from './charts/AtlasChartView';
import AtlasMapView from './maps/AtlasMapView';
import AtlasTableViz from './AtlasTableViz';
import AtlasBriefPanel from './AtlasBriefPanel';
import AtlasNewChatMenu from './AtlasNewChatMenu';
import AbsorptionWidget from '../absorption';
import {
  cancelStream,
  checkStreamStatus,
  downloadSavedFile,
  downloadXlsx,
  fetchAndCacheAllBriefs,
  fetchAtlasImage,
  fetchSavedFilePreviewUrl,
  fetchSavedFileBlob,
  fetchBrief,
  fetchConversationBriefs,
  fetchConversationFiles,
  fetchConversationSavedFiles,
  fetchConversationSnapshot,
  fetchConversationRuntimeDebug,
  fetchConversationToolTraces,
  fetchConversationUploadedFiles,
  fetchUsageLimitStatus,
  fetchViz,
  fetchXlsxRange,
  findActiveStream,
  getIdToken,
  openResumeStream,
  transcribeAudio,
  uploadFile,
  applyWorkspaceScopeToPayload,
  attachFileToConversation,
  generateConversationTitle,
  rollbackConversation,
  listConversationPendingActions,
  listResearchRuns,
  saveBlobAs,
} from './atlasApi';
import LiveResearchView from './research/LiveResearchView';
import { CardDecisionContext, continuationAfterCard, withCardStatus } from './chat/cardDecisions';
import AtlasDocumentPanel from './viewer/AtlasDocumentPanel';
import {
  AtlasContextMeter,
  AtlasExportMenu,
  AtlasInputArea,
  AtlasMessagesList,
  ConversationFilesPanel,
} from './chat/AtlasChatParts';
import { useAtlasBriefs } from './hooks/useAtlasBriefs';
import {
  ATTACHMENT_DUPLICATE_DEBOUNCE_MS,
  MAX_FILE_SIZE,
  accumulateBackgroundEvent,
  addLibraryAttachment,
  applyAttachmentOutcomes,
  applySavedFileToList,
  extractSavedFileFromToolResult,
  extractSavedFilesFromCodeExecution,
  buildToolBadgesFromToolTraces,
  briefsToVersionFormat,
  buildAttachmentSignature,
  buildShareUrl,
  extractClipboardFiles,
  formatFileSize,
  formatToolBadgeInputSummary,
  formatToolCall,
  formatToolResult,
  formatTokenCompact,
  formatTransientToolActivity,
  getFileTypeIcon,
  getToolBadgeKey,
  getToolBadgePresentation,
  isAllowedAttachmentFile,
  isTransientToolActivity,
  newId,
  normalizeAssistantDelta,
  normalizeContextUsage,
} from './chat/helpers';
import { groupSavedFilesByMessage, isAssistantSavedFile } from './chat/savedFileHistory';
import './AtlasChat.css';

const ATLAS_AGENT_API_URL = process.env.REACT_APP_ATLAS_AGENT_API_URL;
// Trailing marker the backend appends to a cancelled turn's partial text.
// Kept in sync with agent.py's interrupted-turn handling.
const INTERRUPTED_MARKER = '[Response interrupted]';

// The streamed thinking summary ("reasoning_trace" line under the working
// indicator) is hidden by default — Atlas narrates between tools in its own
// text, and the summary read as a second voice. Debug escape hatch:
// localStorage.setItem('atlas_show_reasoning_trace', '1').
const SHOW_REASONING_TRACE = (() => {
  try {
    return window.localStorage.getItem('atlas_show_reasoning_trace') === '1';
  } catch {
    return false;
  }
})();

// Research panel opens at ~2/5 of the viewport (never narrower than the
// remembered brief width); the chat column keeps at least 330px.
const RESEARCH_PANEL_OPEN_FRACTION = 0.4;
// The document viewer opens a little wider: a page or slide needs the room.
const DOC_PANEL_OPEN_FRACTION = 0.45;
const MAX_OPEN_DOCUMENTS = 8;

const formatDebugPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.0%';
  return `${n.toFixed(1)}%`;
};

const formatDebugDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDebugChars = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M chars`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k chars`;
  return `${Math.round(n)} chars`;
};

export default function AtlasChat({
  user,
  selectedClient,
  conversationId,
  setConversationId,
  refreshConversations,
  conversations,
  conversationsStatus,
  chatScope,
  clientWriteEnabled,
  hasClientContext,
  dataUserId,
  readOwnerUserIdRef,
  readOwnerUserId,
  clientUserId,
  workspaceContext,
  newChatFocusSignal,
  atlasUserPreferences,
  atlasUserPreferencesLoaded = true,
  atlasMcpConnector,
  onUpdateAtlasUserPreferences,
  // Mobile props
  isMobile,
  onMenuClick,
  briefFullscreenMobile,
  setBriefFullscreenMobile,
  // File Browser attach bridge
  attachFileRef,
  // A reference (e.g. a Land Evaluation) handed over from a tool panel
  chatHandoff,
  onChatHandoffConsumed,
  // Library files attached from the File Browser while a tool panel was open
  pendingLibraryAttachments,
  onPendingLibraryAttachmentsConsumed,
  onChatReadOnlyChange,
  // MainLayout calls forgetConversationRef.current(cid) after a chat is deleted
  forgetConversationRef,
}) {
  const conversationIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // Messages typed while Atlas is responding, held for after the turn ends.
  // Each entry carries its conversation id so a queued draft never leaks
  // into a different conversation after a switch.
  const [queuedDrafts, setQueuedDrafts] = useState([]);
  // Queued drafts flush when a turn ENDS ON ITS OWN. An explicit stop or a
  // steer also drops isStreaming, which would otherwise fire the flush and
  // launch a turn the user never asked for (and, mid-steer, race the steer's
  // own send into a 409). Suppress from the moment we stop until a new turn
  // is actually running.
  const suppressQueueFlushRef = useRef(false);
  // True while a steer is stopping the old turn and waiting for the send gate
  // to clear. Drives the composer's "Stopping current response…" line and
  // gates handleSend so a manual send in that window queues instead of racing.
  const [steerPending, setSteerPending] = useState(false);
  const steerPendingRef = useRef(false);
  // handleQueue is defined below handleSend; hold it in a ref so handleSend
  // can divert into it without a circular dependency.
  const handleQueueRef = useRef(null);
  const handleSteerRef = useRef(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  // Admin read-through: viewing another user's conversation (no sending).
  const [adminViewOnly, setAdminViewOnly] = useState(false);
  const adminViewOnlyRef = useRef(false);
  adminViewOnlyRef.current = adminViewOnly;
  const [error, setError] = useState(null);
  const [billingNotice, setBillingNotice] = useState(null);
  const seenBillingNoticesRef = useRef(new Set());
  const [activity, setActivity] = useState(null);
  // Summarized model reasoning. Deliberately separate from `activity`: that is
  // one slot with last-write-wins, so routing reasoning through it made
  // reasoning and tool progress erase each other. This renders as its own line
  // and persists while a tool runs — the tool says what, this says why.
  const [reasoningTrace, setReasoningTrace] = useState('');
  const [activityKey, setActivityKey] = useState(0);
  const [activityNowTs, setActivityNowTs] = useState(() => Date.now());
  const [contextUsage, setContextUsage] = useState(null);
  const [snapshotViewer, setSnapshotViewer] = useState({
    open: false,
    loading: false,
    error: '',
    data: null,
  });
  const [runtimeDebugViewer, setRuntimeDebugViewer] = useState({
    open: false,
    loading: false,
    error: '',
    data: null,
  });
  const contextUsageRef = useRef(null);

  const abortRef = useRef(null);
  const currentAssistantIdRef = useRef(null);
  // File chips that arrived while no assistant message was attached; flushed
  // onto the message at final_response so they're never silently dropped.
  const pendingSavedFilesRef = useRef([]);
  const vizCacheRef = useRef(new Map());
  const vizTypeRef = useRef(new Map());
  // Cache for all briefs in the current conversation (brief_id -> full brief data)
  const briefCacheRef = useRef(new Map());
  const {
    BRIEF_MAX_WIDTH,
    briefOpen,
    setBriefOpen,
    briefFullscreen,
    briefWidth,
    setBriefWidth,
    activeBriefId,
    setActiveBriefId,
    activeBrief,
    setActiveBrief,
    briefShareUrl,
    setBriefShareUrl,
    briefPublishError,
    setBriefPublishError,
    briefPublishing,
    briefVersions,
    setBriefVersions,
    currentVersionNumber,
    setCurrentVersionNumber,
    handleBriefClose,
    openBriefById,
    handleSelectVersion,
    handleBriefToggleFullscreen,
    handleBriefPublish,
    handleOpenBriefMobile,
    handleBackFromBrief,
    showMobileBriefFullscreen,
  } = useAtlasBriefs({
    isMobile,
    briefFullscreenMobile,
    setBriefFullscreenMobile,
    conversationIdRef,
    readOwnerUserIdRef,
    briefCacheRef,
    chatScope,
    clientWriteEnabled,
    dataUserId,
    workspaceContext,
  });

  // ============== DOCUMENT VIEWER PANEL ==============
  // Third occupant of the brief's side-panel slot (and briefWidth): opening a
  // file closes the brief / research pane and vice versa. Files open as tabs.
  const [docOpen, setDocOpen] = useState(false);
  const [docFullscreen, setDocFullscreen] = useState(false);
  const [docTabs, setDocTabs] = useState([]);
  const [activeDocId, setActiveDocId] = useState(null);

  // ============== LIVE WEB RESEARCH PANEL ==============
  // Shares the single side-panel slot (and briefWidth) with the brief panel:
  // opening one closes the other. Auto-opens on browser_research_start.
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchFullscreen, setResearchFullscreen] = useState(false);
  const [activeResearchRunId, setActiveResearchRunId] = useState(null);
  // This conversation's recent runs (newest first) — powers the panel's tab
  // strip when the agent fired more than one computer-use session.
  const [researchRuns, setResearchRuns] = useState([]);
  // Runs the user explicitly closed: never re-auto-open them.
  const dismissedResearchRunsRef = useRef(new Set());
  // conversationId -> { runId, wasOpen } so switching back can restore the pane.

  const openResearchPanel = useCallback((runId) => {
    setActiveResearchRunId(runId);
    setResearchOpen(true);
    setBriefOpen(false);
    setDocOpen(false);
    // Open wide enough to actually watch a browser: at least 2/5 of the
    // viewport (bounded by the panel max and a 330px chat column), but never
    // shrink a width the user already dragged wider.
    try {
      const viewport = window.innerWidth || 0;
      if (viewport > 0) {
        const target = Math.min(
          BRIEF_MAX_WIDTH,
          viewport - 330,
          Math.round(viewport * RESEARCH_PANEL_OPEN_FRACTION),
        );
        if (target > 320) setBriefWidth((prev) => Math.max(prev, target));
      }
    } catch {
      // window unavailable (SSR/tests) — keep the remembered width.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBriefOpen, setBriefWidth, BRIEF_MAX_WIDTH]);

  // Research state lives in its OWN effect, not loadMessages: loadMessages
  // early-returns when a stream is still active for the target conversation,
  // which would otherwise skip rehydration (e.g. a run Atlas started while
  // this conversation was backgrounded). This always runs on switch, resets
  // the panel (switching closes it by design), and re-arms tabs + the header
  // button from server truth.
  useEffect(() => {
    setResearchOpen(false);
    setResearchFullscreen(false);
    setActiveResearchRunId(null);
    setResearchRuns([]);
    if (!conversationId) return undefined;
    let cancelled = false;
    listResearchRuns({ conversationId, limit: 20 })
      .then((runs) => {
        if (cancelled || conversationIdRef.current !== conversationId) return;
        const list = Array.isArray(runs) ? runs.filter((r) => r?.run_id) : [];
        if (!list.length) return;
        setResearchRuns(list);
        setActiveResearchRunId(list[0].run_id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [conversationId]);

  // Keep the tab strip honest: the mounted view reports the active run's
  // freshest name/status (subagent renames, terminal transitions) so
  // inactive-tab labels and live dots don't go stale.
  const handleResearchRunUpdate = useCallback((update) => {
    if (!update?.run_id) return;
    setResearchRuns((prev) => prev.map((r) => (
      r.run_id === update.run_id
        ? {
            ...r,
            name: update.name || r.name,
            status: update.status && update.status !== 'loading' ? update.status : r.status,
          }
        : r
    )));
  }, []);

  const closeResearchPanel = useCallback(() => {
    setResearchOpen(false);
    setResearchFullscreen(false);
    setActiveResearchRunId((current) => {
      if (current) dismissedResearchRunsRef.current.add(current);
      return current;
    });
  }, []);

  // Brief-side of the mutual exclusion: any brief open closes the research pane.
  const openBriefByIdExclusive = useCallback(
    (briefId) => {
      setResearchOpen(false);
      setDocOpen(false);
      openBriefById(briefId);
    },
    [openBriefById]
  );

  const openDocument = useCallback((file) => {
    if (!file?.file_id || String(file.file_id).startsWith('pending_')) return;
    setDocTabs((prev) => (
      prev.some((d) => d.file_id === file.file_id)
        ? prev
        : [...prev, file].slice(-MAX_OPEN_DOCUMENTS)
    ));
    setActiveDocId(file.file_id);
    setDocOpen(true);
    setBriefOpen(false);
    setResearchOpen(false);
    try {
      const viewport = window.innerWidth || 0;
      if (viewport > 0) {
        const target = Math.min(BRIEF_MAX_WIDTH, viewport - 330, Math.round(viewport * DOC_PANEL_OPEN_FRACTION));
        if (target > 320) setBriefWidth((prev) => Math.max(prev, target));
      }
    } catch {
      // window unavailable (SSR/tests) — keep the remembered width.
    }
  }, [setBriefOpen, setBriefWidth, BRIEF_MAX_WIDTH]);

  const closeDocumentPanel = useCallback(() => {
    setDocOpen(false);
    setDocFullscreen(false);
  }, []);

  const closeDocTab = useCallback((fileId) => {
    setDocTabs((prev) => {
      const idx = prev.findIndex((d) => d.file_id === fileId);
      const next = prev.filter((d) => d.file_id !== fileId);
      if (!next.length) {
        setDocOpen(false);
        setDocFullscreen(false);
        setActiveDocId(null);
      } else {
        setActiveDocId((current) => (current === fileId ? next[Math.min(idx, next.length - 1)].file_id : current));
      }
      return next;
    });
  }, []);

  // One occupant per slot: whatever path opens a brief or the research pane
  // (version picker, stream auto-open, ...) also closes the viewer.
  useEffect(() => {
    if (briefOpen || researchOpen) setDocOpen(false);
  }, [briefOpen, researchOpen]);

  // Open documents belong to the conversation they were opened from.
  useEffect(() => {
    setDocOpen(false);
    setDocFullscreen(false);
    setDocTabs([]);
    setActiveDocId(null);
  }, [conversationId]);

  // ============== CONVERSATION MESSAGES CACHE ==============
  // Cache messages for recent conversations to enable instant switching
  // Structure: Map<conversationId, { messages: Message[], briefs: {...}, savedFiles: [...], loadedAt: number }>
  const conversationCacheRef = useRef(new Map());
  // Chats deleted this session: never cached, restored or reconnected again.
  const deletedConversationIdsRef = useRef(new Set());
  const CACHE_MAX_CONVERSATIONS = 15; // Keep up to 15 conversations in cache
  const CACHE_PRELOAD_COUNT = 5; // Preload first 5 conversations on mount
  const preloadedRef = useRef(false); // Track if initial preload is done
  const linkPreloadedIdsRef = useRef(new Set()); // Track conversation IDs already preloaded from links
  const activityHideTimerRef = useRef(null);
  const splitDragRef = useRef({ dragging: false, startX: 0, startWidth: 520 });
  const messagesContainerRef = useRef(null);
  // The mobile composer is position:fixed, so the thread has to reserve its real
  // height (several lines of text, attachment chips, steer notice, home-indicator
  // gutter) rather than a guessed 68px, or the end of the conversation hides
  // under it. Published as --atlas-composer-h on <html>.
  const composerObserverRef = useRef(null);
  const composerRef = useCallback((node) => {
    composerObserverRef.current?.disconnect();
    composerObserverRef.current = null;
    if (!node) return;
    const publish = () => {
      document.documentElement.style.setProperty('--atlas-composer-h', `${Math.ceil(node.offsetHeight)}px`);
    };
    publish(); // right away, so the first paint already clears it
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    composerObserverRef.current = observer;
  }, []);
  const lastAssistantMsgRef = useRef(null);
  // inputAreaRef: imperative handle for programmatic input (menu prompts, export auto-send)
  const inputAreaRef = useRef(null);

  // ?prompt=… (e.g. the CRM Outreach page's "Open in Atlas") fills the composer once;
  // the user reviews and sends it. The parameter is dropped so a refresh doesn't refill.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prompt = params.get('prompt');
    if (!prompt) return undefined;
    params.delete('prompt');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    const timer = setTimeout(() => inputAreaRef.current?.setInputText(prompt.slice(0, 4000)), 0);
    return () => clearTimeout(timer);
  }, []);

  const [pendingStream, setPendingStream] = useState(null);
  const processedRedisIdsRef = useRef(new Set());
  const isReconnectingRef = useRef(false);
  const reconnectAbortRef = useRef(null);
  
  // Store pending streams per conversation so we can reconnect when user returns
  const pendingStreamsMapRef = useRef(new Map());
  
  // Track current streaming message state synchronously (for saving on conversation switch)
  const currentStreamingMsgRef = useRef({
    content: '',
    toolBadges: [],
    toolRuns: {},
    citationsMap: {}
  });

  const [showFinishedNotice, setShowFinishedNotice] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  // File upload state - files are stored pending until message is sent
  const [attachedFiles, setAttachedFiles] = useState([]);
  const attachedFilesRef = useRef(attachedFiles);
  attachedFilesRef.current = attachedFiles;
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const recentAttachmentSignaturesRef = useRef(new Map());
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [companyConversationConfirmOpen, setCompanyConversationConfirmOpen] = useState(false);
  const [companyConversationConfirmed, setCompanyConversationConfirmed] = useState(false);
  const [companyConversationDontShowAgain, setCompanyConversationDontShowAgain] = useState(false);
  // A message sent before the company-conversation prompt was answered: it goes out on Proceed.
  const pendingCompanyMessageRef = useRef(null);
  const [usageLimitPrompt, setUsageLimitPrompt] = useState(null);

  // Conversation files panel state
  const [conversationFiles, setConversationFiles] = useState([]);
  const [filesDropdownOpen, setFilesDropdownOpen] = useState(false);

  // Scroll-to-top pagination state
  const loadMoreSentinelRef = useRef(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const oldestMessageIdRef = useRef(null);

  // Impersonated chat is removed: admin chats always post to the admin's own
  // account (the backend rejects act_as_user_id/data_user_id on chat). The
  // client-view file library stays read-scoped to the selected user.
  const shouldWriteAsSelectedClient = false;
  const isClientReadOnly = false;
  const welcomeFirstName = useMemo(() => {
    const rawName = typeof user?.name === 'string' ? user.name.trim() : '';
    if (!rawName) return '';
    const firstToken = rawName.split(/\s+/)[0] || '';
    if (!firstToken || firstToken.includes('@')) return '';
    return firstToken;
  }, [user?.name]);
  const welcomeTitle = welcomeFirstName ? `Welcome Back, ${welcomeFirstName}` : 'Welcome to Atlas';
  const isWelcomeState = messages.length === 0 && !isStreaming && !conversationId;
  const canShowIntroModal = !user?.isAdmin && !hasClientContext && !workspaceContext?.isCompanyScope;
  const companyConversationName = workspaceContext?.companyName || 'your company';
  const requiresCompanyConversationConfirm = Boolean(
    workspaceContext?.isCompanyScope
      && isWelcomeState
      && atlasUserPreferencesLoaded
      && !companyConversationConfirmed
      && atlasUserPreferences?.prompt_on_new_company_chat !== false
  );
  const focusAtlasInput = useCallback(() => {
    if (isMobile || isClientReadOnly) return;
    window.setTimeout(() => {
      inputAreaRef.current?.focus();
    }, 80);
  }, [isClientReadOnly, isMobile]);

  useEffect(() => {
    if (!canShowIntroModal) return;
    if (!isWelcomeState) return;
    if (chatScope !== 'me') return;
    if (conversationsStatus !== 'ready') return;
    if (!Array.isArray(conversations) || conversations.length > 0) return;
    setShowIntroModal(true);
  }, [canShowIntroModal, chatScope, conversations, conversationsStatus, isWelcomeState]);

  useEffect(() => {
    if (!isWelcomeState) return;
    if (showIntroModal) return;
    if (companyConversationConfirmOpen) return;
    focusAtlasInput();
  }, [companyConversationConfirmOpen, focusAtlasInput, isWelcomeState, newChatFocusSignal, showIntroModal]);

  useEffect(() => {
    setCompanyConversationConfirmed(false);
    setCompanyConversationDontShowAgain(false);
    setCompanyConversationConfirmOpen(false);
    pendingCompanyMessageRef.current = null;
  }, [newChatFocusSignal, workspaceContext?.companyId, workspaceContext?.scopeType]);

  // Re-applied after the reset above too: "New chat" while the prompt is up must not
  // leave it closed with the confirmation still owed (the send would then stop for it).
  useEffect(() => {
    setCompanyConversationConfirmOpen(requiresCompanyConversationConfirm);
  }, [requiresCompanyConversationConfirm, newChatFocusSignal, workspaceContext?.companyId, workspaceContext?.scopeType]);

  useEffect(() => {
    if (!workspaceContext?.isCompanyScope) return;
    const companyLabel = workspaceContext.companyName || 'Company';
    const previous = document.title;
    document.title = `Atlas · ${companyLabel}`;
    return () => {
      document.title = previous;
    };
  }, [workspaceContext?.isCompanyScope, workspaceContext?.companyName]);

  const dismissIntroModal = useCallback(() => {
    setShowIntroModal(false);
  }, []);

  const confirmCompanyConversation = useCallback(async () => {
    if (companyConversationDontShowAgain) {
      try {
        await onUpdateAtlasUserPreferences?.({ prompt_on_new_company_chat: false });
      } catch (err) {
        console.warn('Failed to save Atlas user preferences:', err);
      }
    }
    setCompanyConversationConfirmed(true);
    setCompanyConversationConfirmOpen(false);
    focusAtlasInput();
  }, [companyConversationDontShowAgain, focusAtlasInput, onUpdateAtlasUserPreferences]);

  const promptForCompanyConversationConfirm = useCallback(() => {
    if (!requiresCompanyConversationConfirm) return false;
    setCompanyConversationConfirmOpen(true);
    return true;
  }, [requiresCompanyConversationConfirm]);

  const introModalNode = canShowIntroModal && showIntroModal && isWelcomeState ? (
    <div className="atlas-intro-modal-overlay" role="presentation" onClick={dismissIntroModal}>
      <div
        className="atlas-intro-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-intro-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="atlas-intro-modal-close"
          onClick={dismissIntroModal}
          aria-label="Close welcome message"
        >
          <IoCloseOutline size={20} />
        </button>
        <div className="atlas-intro-modal-eyebrow">Atlas by Real Torch</div>
        <h2 id="atlas-intro-modal-title" className="atlas-intro-modal-title">
          Welcome to Atlas by Real Torch
        </h2>
        <p className="atlas-intro-modal-copy">
          Try Atlas using 10 free queries. After that, reach out to{' '}
          <a href="mailto:admin@realtorch.ai">admin@realtorch.ai</a> to get your account set up for full access.
        </p>
        <div className="atlas-intro-modal-actions">
          <button type="button" className="atlas-intro-modal-primary" onClick={dismissIntroModal}>
            Start exploring
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const companyConversationConfirmNode = companyConversationConfirmOpen ? (
    <div className="atlas-company-confirm-overlay" role="presentation">
      <div
        className="atlas-company-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-company-confirm-title"
      >
        <div className="atlas-company-confirm-eyebrow">Company mode</div>
        <h2 id="atlas-company-confirm-title" className="atlas-company-confirm-title">
          Start a company conversation?
        </h2>
        <p className="atlas-company-confirm-copy">
          The files uploaded and conversation content will be made accessible by other members of {companyConversationName}. Proceed?
        </p>
        <label className="atlas-company-confirm-check">
          <input
            type="checkbox"
            checked={companyConversationDontShowAgain}
            onChange={(event) => setCompanyConversationDontShowAgain(event.target.checked)}
          />
          <span>Don't show me this again in company mode</span>
        </label>
        {companyConversationDontShowAgain && (
          <div className="atlas-company-confirm-helper">
            This can be changed in user settings.
          </div>
        )}
        <div className="atlas-company-confirm-actions">
          <button type="button" className="atlas-company-confirm-primary" onClick={confirmCompanyConversation}>
            Proceed
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // Clear attached files when conversation changes
  useEffect(() => {
    setAttachedFiles([]);
    attachedFilesRef.current = [];
    setFileUploadError(null);
    recentAttachmentSignaturesRef.current.clear();
  }, [conversationId]);

  // A Land Evaluation handed over from the tool panel: attach it as a reference chip.
  // References are not files: nothing uploads, the backend gets {kind, request_id, address}.
  useEffect(() => {
    if (!chatHandoff || chatHandoff.kind !== 'land_evaluation' || !chatHandoff.request_id) return;
    const id = `land_evaluation_${chatHandoff.request_id}`;
    setAttachedFiles(prev => (prev.some(f => f.id === id) ? prev : [...prev, {
      id,
      file: null,
      name: chatHandoff.address || chatHandoff.request_id,
      size: 0,
      mimeType: 'application/x-land-evaluation',
      status: 'reference',
      kind: 'land_evaluation',
      requestId: chatHandoff.request_id,
      address: chatHandoff.address || '',
    }]));
    if (typeof onChatHandoffConsumed === 'function') onChatHandoffConsumed();
  }, [chatHandoff, onChatHandoffConsumed]);

  // File Browser -> composer. Returns what happened so the browser only says
  // "Added" when the file really is in the composer.
  //   fileInfo: { file_id, filename, mime_type, size_bytes, workspace_scope_type, company_id }
  const addLibraryFileToComposer = useCallback((fileInfo) => {
    if (!fileInfo?.file_id) return { status: 'error', error: 'This file cannot be attached.' };
    // A chat that can't send (read-only client view, admin viewing someone
    // else's conversation) can't take attachments either.
    if (isClientReadOnly || adminViewOnlyRef.current) {
      return { status: 'error', error: "This conversation is read-only, so files can't be attached." };
    }
    const result = addLibraryAttachment(attachedFilesRef.current, fileInfo);
    if (result.duplicate) return { status: 'duplicate' };
    // Mirror into the ref now so a quick second click is still caught as a duplicate.
    attachedFilesRef.current = result.attachments;
    setAttachedFiles((prev) => addLibraryAttachment(prev, fileInfo).attachments);
    setFileUploadError(null);
    return { status: 'attached' };
  }, [isClientReadOnly]);

  // Tell MainLayout whether this chat can take attachments, so a file attached
  // while a tool panel hides the chat isn't queued for a read-only conversation.
  useEffect(() => {
    if (typeof onChatReadOnlyChange !== 'function') return;
    onChatReadOnlyChange({ conversationId: conversationId || null, readOnly: Boolean(isClientReadOnly || adminViewOnly) });
  }, [adminViewOnly, conversationId, isClientReadOnly, onChatReadOnlyChange]);

  // Wire up attachFileRef so File Browser can add files to input area. The ref
  // is cleared on unmount (a tool panel replaces the chat), so MainLayout queues
  // the file instead of calling a dead closure.
  useEffect(() => {
    if (!attachFileRef) return undefined;
    attachFileRef.current = addLibraryFileToComposer;
    return () => {
      if (attachFileRef.current === addLibraryFileToComposer) attachFileRef.current = null;
    };
  }, [attachFileRef, addLibraryFileToComposer]);

  // Files attached from the File Browser while the chat was not mounted.
  useEffect(() => {
    if (!Array.isArray(pendingLibraryAttachments) || pendingLibraryAttachments.length === 0) return;
    pendingLibraryAttachments.forEach((fileInfo) => addLibraryFileToComposer(fileInfo));
    if (typeof onPendingLibraryAttachmentsConsumed === 'function') onPendingLibraryAttachmentsConsumed();
  }, [addLibraryFileToComposer, onPendingLibraryAttachmentsConsumed, pendingLibraryAttachments]);

  // ============== CACHE MANAGEMENT FUNCTIONS ==============

  // Assemble raw API data into the cache-ready format
  const assembleConversationData = useCallback(({ items, briefsList, savedFiles, uploadedFiles, allConversationFiles, contextUsageRaw, pendingActions, viewerIsOwner }) => {
    const contextUsage = normalizeContextUsage(contextUsageRaw);

    // Build briefs lookup by message_id
    const briefsByMessageId = {};
    if (briefsList) {
      for (const b of briefsList) {
        if (b.message_id && !briefsByMessageId[b.message_id]) {
          briefsByMessageId[b.message_id] = b;
        }
      }
    }

    // Build pending email-approval lookup by message_id so approval cards survive
    // a page refresh (the live artifact event only fires during the streaming turn).
    // A single message can stage multiple emails, so collect them as a list.
    const pendingEmailsByMessageId = {};
    const pendingAppActionsByMessageId = {};
    const pendingConnectsByMessageId = {};
    for (const a of pendingActions || []) {
      if (a?.message_id == null) continue;
      const bucket = a?.kind === 'email_send'
        ? pendingEmailsByMessageId
        : a?.kind === 'app_action'
          ? pendingAppActionsByMessageId
          : a?.kind === 'app_connect'
            ? pendingConnectsByMessageId
            : null;
      if (!bucket) continue;
      if (!bucket[a.message_id]) bucket[a.message_id] = [];
      bucket[a.message_id].push(a);
    }

    const { savedFilesByMessageId, savedFilesWithoutMessageId } = groupSavedFilesByMessage({
      savedFiles,
      uploadedFiles,
    });

    // Group uploaded files by message_id (for user messages)
    const uploadedFilesByMessageId = {};
    for (const f of uploadedFiles) {
      // Skip assistant-returned/generated files — they're handled above as savedFiles
      if (isAssistantSavedFile(f)) continue;
      const fileData = {
        file_id: f.file_id,
        filename: f.filename,
        size_bytes: f.size_bytes,
        mime_type: f.mime_type,
      };
      if (f.message_id) {
        if (!uploadedFilesByMessageId[f.message_id]) uploadedFilesByMessageId[f.message_id] = [];
        uploadedFilesByMessageId[f.message_id].push(fileData);
      }
    }

    // Build messages array
    const messages = items.map((m, idx) => {
      const role = m?.role === 'assistant' ? 'assistant' : 'user';
      const msgId = m?.message_id;
      const linkedBrief = msgId ? briefsByMessageId[msgId] : null;
      const messageFiles = msgId ? (savedFilesByMessageId[msgId] || []) : [];
      const userUploadedFiles = msgId ? (uploadedFilesByMessageId[msgId] || []) : [];
      const toolTraces = Array.isArray(m?.tool_traces) ? m.tool_traces : [];
      const pendingEmails = msgId != null ? (pendingEmailsByMessageId[msgId] || []) : [];
      const pendingAppActions = msgId != null ? (pendingAppActionsByMessageId[msgId] || []) : [];
      const pendingConnects = msgId != null ? (pendingConnectsByMessageId[msgId] || []) : [];
      // The backend marks a cancelled turn's partial text with a trailing
      // "[Response interrupted]". Convert it back to the flag the live path
      // uses so history renders the same styled note instead of raw marker
      // text — otherwise stopping/steering looks different after a refresh.
      let content = m?.content || '';
      let wasInterrupted = false;
      if (role === 'assistant' && content.trimEnd().endsWith(INTERRUPTED_MARKER)) {
        content = content.trimEnd().slice(0, -INTERRUPTED_MARKER.length).trimEnd();
        wasInterrupted = true;
      }
      return {
        id: msgId ? `hist_${msgId}` : `hist_${idx}`,
        dbMessageId: msgId || null,
        role,
        content,
        interrupted: wasInterrupted,
        createdAt: m?.created_at ? new Date(m.created_at).getTime() : null,
        finalized: true,
        toolBadges: role === 'assistant' ? buildToolBadgesFromToolTraces(toolTraces) : [],
        toolRuns: {},
        toolTraces,
        toolTraceDetailsLoaded: false,
        citationsMap: {},
        briefId: linkedBrief?.brief_id || null,
        briefTitle: linkedBrief?.title || null,
        savedFiles: messageFiles,
        uploadedFiles: userUploadedFiles,
        emailApprovals: pendingEmails.length
          ? pendingEmails.map((a) => ({ actionId: a.action_id, preview: a.preview || {}, status: a.status || 'pending' }))
          : undefined,
        appApprovals: pendingAppActions.length
          ? pendingAppActions.map((a) => ({ actionId: a.action_id, preview: a.preview || {}, status: a.status || 'pending' }))
          : undefined,
        appConnects: pendingConnects.length
          ? pendingConnects.map((a) => ({ actionId: a.action_id, preview: a.preview || {}, status: a.status || 'pending' }))
          : undefined,
      };
    });

    // Attach orphan saved files to last assistant message
    if (savedFilesWithoutMessageId.length > 0) {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          messages[i].savedFiles = [...messages[i].savedFiles, ...savedFilesWithoutMessageId];
          break;
        }
      }
    }

    return {
      messages,
      briefsByMessageId,
      savedFiles,
      conversationFiles: allConversationFiles,
      contextUsage,
      viewerIsOwner: viewerIsOwner !== false,
      loadedAt: Date.now()
    };
  }, []);

  // Load a single conversation's data (messages, briefs, saved files)
  // Tries the consolidated /bundle endpoint first, falls back to parallel individual requests.
  const loadConversationData = useCallback(async (cid, ownerUserId, { limit = 400 } = {}) => {
    if (!cid || !ATLAS_AGENT_API_URL) return null;

    try {
      const idToken = await getIdToken();
      const msgLimit = String(limit);

      // Try consolidated bundle endpoint first (single HTTP request)
      const bundleUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(cid)}/bundle`);
      bundleUrl.searchParams.set('limit', msgLimit);
      if (ownerUserId) bundleUrl.searchParams.set('owner_user_id', String(ownerUserId));
      if (workspaceContext?.scopeType === 'company' && workspaceContext?.companyId) {
        bundleUrl.searchParams.set('scope_type', 'company');
        bundleUrl.searchParams.set('company_id', String(workspaceContext.companyId));
      } else {
        bundleUrl.searchParams.set('scope_type', 'user');
      }

      // Fetch staged approvals (email sends and connected-app changes) in parallel so
      // their cards survive a refresh. No status filter -> also restore done/cancelled/
      // superseded cards so the conversation keeps a record of what happened.
      const pendingPromise = listConversationPendingActions({
        conversationId: cid,
      }).catch(() => ({ pending_actions: [] }));

      const bundleRes = await fetch(bundleUrl.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (bundleRes.ok) {
        const data = await bundleRes.json();
        const pendingRes = await pendingPromise;
        const assembled = assembleConversationData({
          items: Array.isArray(data?.messages) ? data.messages : [],
          briefsList: data?.briefs || [],
          savedFiles: data?.saved_files || [],
          uploadedFiles: data?.uploaded_files || [],
          allConversationFiles: data?.files || [],
          contextUsageRaw: data?.context_usage,
          pendingActions: pendingRes?.pending_actions || [],
          viewerIsOwner: data?.viewer_is_owner,
        });
        if (assembled) assembled.ownerContext = ownerUserId ?? null;
        return assembled;
      }

      // Fallback: parallel individual requests (e.g., older backend without /bundle)
      const msgUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(cid)}/messages`);
      msgUrl.searchParams.set('limit', msgLimit);
      if (ownerUserId) msgUrl.searchParams.set('owner_user_id', String(ownerUserId));

      const [msgRes, briefsRes, savedFiles, uploadedFiles, allConversationFiles, pendingRes] = await Promise.all([
        fetch(msgUrl.toString(), {
          method: 'GET',
          headers: { Authorization: `Bearer ${idToken}` }
        }),
        fetchConversationBriefs({ conversationId: cid, ownerUserId, idToken, workspaceContext }).catch(() => ({ briefs: [] })),
        fetchConversationSavedFiles({ conversationId: cid, ownerUserId, idToken }).catch(() => []),
        fetchConversationUploadedFiles({ conversationId: cid, ownerUserId, idToken }).catch(() => []),
        fetchConversationFiles({ conversationId: cid, ownerUserId, idToken, workspaceContext }).catch(() => []),
        pendingPromise,
      ]);

      if (!msgRes.ok) return null;
      const msgData = await msgRes.json();

      const assembledFallback = assembleConversationData({
        items: Array.isArray(msgData?.messages) ? msgData.messages : [],
        briefsList: briefsRes?.briefs || [],
        savedFiles,
        uploadedFiles,
        allConversationFiles,
        contextUsageRaw: msgData?.context_usage,
        pendingActions: pendingRes?.pending_actions || [],
        viewerIsOwner: msgData?.viewer_is_owner,
      });
      if (assembledFallback) assembledFallback.ownerContext = ownerUserId ?? null;
      return assembledFallback;
    } catch (err) {
      console.warn(`[AtlasChat] Failed to load conversation ${cid}:`, err);
      return null;
    }
  }, [assembleConversationData, workspaceContext]);
  
  // Add data to cache (with LRU eviction)
  const cacheConversation = useCallback((cid, data) => {
    if (!cid || !data) return;
    if (deletedConversationIdsRef.current.has(cid)) return;
    
    // Evict oldest if at capacity
    if (conversationCacheRef.current.size >= CACHE_MAX_CONVERSATIONS) {
      let oldestKey = null;
      let oldestTime = Infinity;
      for (const [key, val] of conversationCacheRef.current.entries()) {
        if (val.loadedAt < oldestTime) {
          oldestTime = val.loadedAt;
          oldestKey = key;
        }
      }
      if (oldestKey) conversationCacheRef.current.delete(oldestKey);
    }
    
    conversationCacheRef.current.set(cid, data);
  }, []);
  
  // Get from cache (and update access time)
  const getCachedConversation = useCallback((cid) => {
    if (deletedConversationIdsRef.current.has(cid)) {
      conversationCacheRef.current.delete(cid);
      return null;
    }
    const cached = conversationCacheRef.current.get(cid);
    // A cache entry loaded under a different acting-as context is stale
    // (viewerIsOwner/read-only state depends on who we're acting as).
    if (cached && String(cached.ownerContext ?? '') !== String(readOwnerUserIdRef?.current ?? '')) {
      conversationCacheRef.current.delete(cid);
      return null;
    }
    if (cached) {
      // Update access time for LRU
      cached.loadedAt = Date.now();
    }
    return cached;
  }, []);
  
  // Preload recent conversations on mount
  useEffect(() => {
    if (preloadedRef.current) return;
    if (!conversations || conversations.length === 0) return;
    
    preloadedRef.current = true;
    const ownerUserId = readOwnerUserIdRef?.current;
    
    // Preload first N conversations in parallel
    const toPreload = conversations.slice(0, CACHE_PRELOAD_COUNT);
    
    Promise.all(
      toPreload.map(async (conv) => {
        const cid = conv.conversation_id;
        if (!cid || conversationCacheRef.current.has(cid)) return;

        // Use smaller message limit for preloaded conversations (recent messages only)
        const data = await loadConversationData(cid, ownerUserId, { limit: 200 });
        if (data) {
          cacheConversation(cid, data);
        }
      })
    ).then(() => {
      console.log(`[AtlasChat] Preloaded ${toPreload.length} conversations into cache`);
    });
  }, [conversations, loadConversationData, cacheConversation, readOwnerUserIdRef]);

  // Preload conversations linked in streamed text (e.g. /c/{uuid} from history tools)
  const preloadLinkedConversations = useCallback((text) => {
    const pattern = /\/c\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const cid = match[1];
      if (linkPreloadedIdsRef.current.has(cid)) continue;
      if (conversationCacheRef.current.has(cid)) continue;
      if (cid === conversationIdRef.current) continue;
      linkPreloadedIdsRef.current.add(cid);
      const ownerUserId = readOwnerUserIdRef?.current;
      loadConversationData(cid, ownerUserId, { limit: 200 }).then((data) => {
        if (data) {
          cacheConversation(cid, data);
          console.log(`[AtlasChat] Preloaded linked conversation ${cid}`);
        }
      }).catch(() => {});
    }
  }, [loadConversationData, cacheConversation, readOwnerUserIdRef]);

  // Load older messages when user scrolls to top (pagination)
  const loadOlderMessages = useCallback(async () => {
    const cid = conversationIdRef.current;
    if (!cid || !hasMoreMessages || isLoadingMoreRef.current || isStreaming) return;

    const oldest = oldestMessageIdRef.current;
    if (!oldest) return;

    isLoadingMoreRef.current = true;
    try {
      const idToken = await getIdToken();
      const ownerUserId = readOwnerUserIdRef?.current;

      // Fetch older messages via bundle (with before_message_id pagination)
      const bundleUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(cid)}/bundle`);
      bundleUrl.searchParams.set('limit', '50');
      bundleUrl.searchParams.set('before_message_id', String(oldest));
      if (ownerUserId) bundleUrl.searchParams.set('owner_user_id', String(ownerUserId));

      const res = await fetch(bundleUrl.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (!res.ok) {
        isLoadingMoreRef.current = false;
        return;
      }

      const data = await res.json();
      const olderItems = Array.isArray(data?.messages) ? data.messages : [];

      if (olderItems.length === 0) {
        setHasMoreMessages(false);
        isLoadingMoreRef.current = false;
        return;
      }

      // Build message objects for older messages (no briefs/files needed — already loaded)
      const olderMessages = olderItems.map((m, idx) => {
        const role = m?.role === 'assistant' ? 'assistant' : 'user';
        const msgId = m?.message_id;
        return {
          id: msgId ? `hist_${msgId}` : `hist_older_${idx}`,
          role,
          content: m?.content || '',
          finalized: true,
          toolBadges: [],
          toolRuns: {},
          citationsMap: {},
          briefId: null,
          briefTitle: null,
          savedFiles: [],
          uploadedFiles: [],
        };
      });

      // Preserve scroll position: measure before prepending
      const container = messagesContainerRef.current;
      const prevScrollHeight = container ? container.scrollHeight : 0;

      setMessages(prev => [...olderMessages, ...prev]);

      // Update oldest message ID for next pagination
      const newOldest = olderItems[0]?.message_id;
      if (newOldest) oldestMessageIdRef.current = newOldest;

      // If fewer than requested, no more to load
      if (olderItems.length < 50) setHasMoreMessages(false);

      // Restore scroll position after prepend
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - prevScrollHeight;
        }
      });
    } catch (err) {
      console.warn('[AtlasChat] Failed to load older messages:', err);
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [hasMoreMessages, isStreaming, readOwnerUserIdRef]);

  // IntersectionObserver: trigger loading older messages when user scrolls to top
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const container = messagesContainerRef.current;
    if (!sentinel || !container || !hasMoreMessages) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadOlderMessages();
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreMessages, loadOlderMessages]);

  // Voice input: transcript only; AtlasInputArea puts it in the composer.
  const handleTranscribe = useCallback(async (blob, { durationMs } = {}) => {
    const result = await transcribeAudio({ blob, conversationId, durationMs });
    return result?.text || '';
  }, [conversationId]);

  // Handle file selection - store in memory until message is sent
  const handleFileUpload = useCallback((files) => {
    if (!files || files.length === 0) return;
    if (promptForCompanyConversationConfirm()) return;
    if (isClientReadOnly) {
      setFileUploadError('Cannot upload files in read-only mode');
      return;
    }

    const now = Date.now();
    const minTs = now - ATTACHMENT_DUPLICATE_DEBOUNCE_MS;
    for (const [sig, ts] of recentAttachmentSignaturesRef.current.entries()) {
      if (ts < minTs) {
        recentAttachmentSignaturesRef.current.delete(sig);
      }
    }

    // Validate files and add to pending attachments
    const newAttachments = [];
    for (const file of files) {
      const sig = buildAttachmentSignature(file);
      const lastSeenTs = sig ? recentAttachmentSignaturesRef.current.get(sig) : null;
      if (lastSeenTs && (now - lastSeenTs) < ATTACHMENT_DUPLICATE_DEBOUNCE_MS) {
        continue;
      }

      if (!isAllowedAttachmentFile(file)) {
        setFileUploadError(`Unsupported file type: ${file.type || file.name}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileUploadError(`File too large: ${file.name} (max 500 MB)`);
        continue;
      }

      if (sig) {
        recentAttachmentSignaturesRef.current.set(sig, now);
      }

      // Store the File object for later upload when message is sent
      newAttachments.push({
        id: `pending_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        file: file,  // Keep the actual File object
        name: file.name,
        size: file.size,
        mimeType: file.type || (String(file.name || '').toLowerCase().endsWith('.svg') ? 'image/svg+xml' : ''),
        status: 'pending',  // Not uploaded yet
      });
    }

    if (newAttachments.length > 0) {
      setAttachedFiles(prev => [...prev, ...newAttachments]);
      setFileUploadError(null);
    }
  }, [isClientReadOnly, promptForCompanyConversationConfirm]);

  // Drag & drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isClientReadOnly && !isStreaming) {
      setIsDragOver(true);
    }
  }, [isClientReadOnly, isStreaming]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isClientReadOnly || isStreaming) return;

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [isClientReadOnly, isStreaming, handleFileUpload]);

  const handleClipboardPaste = useCallback((e) => {
    if (isClientReadOnly || isStreaming) return;
    const files = extractClipboardFiles(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    e.stopPropagation();
    handleFileUpload(files);
  }, [handleFileUpload, isClientReadOnly, isStreaming]);

  useEffect(() => {
    window.addEventListener('paste', handleClipboardPaste);
    return () => window.removeEventListener('paste', handleClipboardPaste);
  }, [handleClipboardPaste]);

  // Remove attached file
  const handleRemoveFile = useCallback((fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const scrollToLastAssistant = useCallback(() => {
    if (lastAssistantMsgRef.current) {
      lastAssistantMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowFinishedNotice(false);
  }, []);

  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 100;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  const isLastAssistantVisible = useCallback(() => {
    const container = messagesContainerRef.current;
    const lastMsg = lastAssistantMsgRef.current;
    if (!container || !lastMsg) return false;

    const containerRect = container.getBoundingClientRect();
    const msgRect = lastMsg.getBoundingClientRect();

    return msgRect.top >= containerRect.top && msgRect.top <= containerRect.bottom;
  }, []);

  // The keyboard sliding up shortens the visible message list. If the user was
  // reading the bottom of the thread, keep them there instead of leaving the
  // newest message hidden behind the keyboard.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    let lastHeight = viewport.height;
    const handleViewportResize = () => {
      const shrank = viewport.height < lastHeight;
      lastHeight = viewport.height;
      if (shrank && isNearBottom()) {
        // Wait for the composer to settle at its new offset before measuring.
        window.requestAnimationFrame(scrollToBottom);
      }
    };

    viewport.addEventListener('resize', handleViewportResize);
    return () => viewport.removeEventListener('resize', handleViewportResize);
  }, [isNearBottom, scrollToBottom]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    vizCacheRef.current = new Map();
    vizTypeRef.current = new Map();
  }, [conversationId]);

  // Constrain briefWidth to actual container dimensions on mount and resize
  useEffect(() => {
    const constrainBriefWidth = () => {
      const splitContainer = document.querySelector('.atlas-chat-split');
      if (!splitContainer) return;
      const containerWidth = splitContainer.offsetWidth;
      // Reserve 330px for chat (280px min) + divider (10px) + small buffer (40px)
      // Also cap at BRIEF_MAX_WIDTH to match fullscreen mode
      const maxWidth = Math.min(BRIEF_MAX_WIDTH, containerWidth - 330);
      setBriefWidth((prev) => Math.max(320, Math.min(maxWidth, prev)));
    };
    
    // Run once after mount
    constrainBriefWidth();
    
    // Also run on window resize
    window.addEventListener('resize', constrainBriefWidth);
    return () => window.removeEventListener('resize', constrainBriefWidth);
  }, [BRIEF_MAX_WIDTH, setBriefWidth]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      if (showFinishedNotice && (isNearBottom() || isLastAssistantVisible())) {
        setShowFinishedNotice(false);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [showFinishedNotice, isNearBottom, isLastAssistantVisible]);

  useEffect(() => {
    if (!activity?.spinning) return undefined;
    const id = window.setInterval(() => setActivityNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activity?.spinning]);

  useEffect(() => {
    contextUsageRef.current = contextUsage;
  }, [contextUsage]);

  useEffect(() => {
    function onMove(e) {
      if (!splitDragRef.current.dragging) return;
      const dx = splitDragRef.current.startX - e.clientX;
      // Get the actual available width by finding the split container
      const splitContainer = document.querySelector('.atlas-chat-split');
      const containerWidth = splitContainer ? splitContainer.offsetWidth : window.innerWidth - 280;
      // Reserve space for chat (min 280px) + divider (10px) + buffer (40px) = 330px
      // Also hard cap at BRIEF_MAX_WIDTH to prevent brief from getting too wide
      const maxWidth = Math.min(BRIEF_MAX_WIDTH, containerWidth - 330);
      const next = Math.max(320, Math.min(maxWidth, splitDragRef.current.startWidth + dx));
      setBriefWidth(next);
    }
    function onUp() {
      if (!splitDragRef.current.dragging) return;
      splitDragRef.current.dragging = false;
      document.body.classList.remove('atlas-resizing');
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [BRIEF_MAX_WIDTH, setBriefWidth]);

  // Track which conversation the current stream belongs to
  const streamConversationIdRef = useRef(null);

  // Track when a message is being sent from the welcome screen (conversationId=null).
  // This distinguishes "welcome-screen send waiting for conversation_started" from
  // "user intentionally clicked + to start a new chat while streaming".
  const sendingFromWelcomeRef = useRef(false);
  const stoppedConversationsRef = useRef(new Set());
  const backgroundStreamsRef = useRef(new Map());

  // Detach active stream to background — keeps HTTP connection alive.
  // Shared by new-chat and conversation-switching paths.
  const detachActiveStream = useCallback(() => {
    const switchingFromConversationId = streamConversationIdRef.current;
    const switchingFromAssistantId = currentAssistantIdRef.current;
    const hasActiveStream = switchingFromConversationId && switchingFromAssistantId;

    if (hasActiveStream) {
      let existing = pendingStreamsMapRef.current.get(switchingFromConversationId);
      if (!existing) {
        for (const [, entry] of pendingStreamsMapRef.current.entries()) {
          if (entry.conversationId === switchingFromConversationId) {
            existing = entry;
            break;
          }
        }
      }
      // Save to pendingStreamsMapRef for Redis reconnection fallback (page refresh)
      const savedState = {
        conversationId: switchingFromConversationId,
        messageId: existing?.messageId || null,
        assistantMsgId: existing?.assistantMsgId || switchingFromAssistantId,
        lastRedisId: existing?.lastRedisId || '0',
        accumulatedContent: currentStreamingMsgRef.current.content || '',
        toolBadges: currentStreamingMsgRef.current.toolBadges || [],
        toolRuns: currentStreamingMsgRef.current.toolRuns || {},
        citationsMap: currentStreamingMsgRef.current.citationsMap || {}
      };
      pendingStreamsMapRef.current.set(switchingFromConversationId, savedState);

      // Cache current messages for instant restore
      setMessages((currentMessages) => {
        if (currentMessages.length > 0 && switchingFromConversationId) {
          conversationCacheRef.current.set(switchingFromConversationId, {
            messages: currentMessages,
            briefsByMessageId: {},
            savedFiles: [],
            contextUsage: contextUsageRef.current || null,
            loadedAt: Date.now()
          });
        }
        return currentMessages;
      });

      // Create background stream entry — reader loop will switch to accumulation mode
      const bgEntry = {
        abortController: abortRef.current || reconnectAbortRef.current,
        assistantMsgId: switchingFromAssistantId,
        messageId: existing?.messageId || null,
        accumulatedContent: currentStreamingMsgRef.current.content || '',
        toolBadges: [...(currentStreamingMsgRef.current.toolBadges || [])],
        toolRuns: { ...(currentStreamingMsgRef.current.toolRuns || {}) },
        citationsMap: { ...(currentStreamingMsgRef.current.citationsMap || {}) },
        savedFiles: [],
        completed: false,
        finalData: null,
        contextUsage: null,
        _bgMapRef: backgroundStreamsRef.current, // for conversation_started re-keying
      };
      backgroundStreamsRef.current.set(switchingFromConversationId, bgEntry);
    }

    // Clear refs for the new conversation — but DO NOT abort the HTTP connection
    abortRef.current = null;
    reconnectAbortRef.current = null;
    currentAssistantIdRef.current = null;
    streamConversationIdRef.current = null;
    sendingFromWelcomeRef.current = false;
    isReconnectingRef.current = false;
    setIsStreaming(false);
    setPendingStream(null);
    setError(null);
    setActivity(null);
    setReasoningTrace('');
  }, []);

  // Promote a background stream back to foreground when user switches back.
  // Instantly restores accumulated content and re-enables live streaming.
  const promoteBackgroundStream = useCallback((cid, bgStream) => {
    // Apply accumulated content to cached messages
    const cached = conversationCacheRef.current.get(cid);
    if (cached) {
      const updatedMessages = cached.messages.map((m) => {
        if (m.id !== bgStream.assistantMsgId) return m;
        // Merge in file chips accumulated while backgrounded.
        let savedFiles = m.savedFiles;
        for (const fileInfo of bgStream.savedFiles || []) {
          savedFiles = applySavedFileToList(savedFiles, fileInfo);
        }
        return {
          ...m,
          content: bgStream.accumulatedContent,
          finalized: false,
          toolBadges: bgStream.toolBadges,
          toolRuns: bgStream.toolRuns,
          citationsMap: bgStream.citationsMap,
          ...(savedFiles ? { savedFiles } : {}),
        };
      });
      setMessages(updatedMessages);
    }

    // Restore streaming refs so the reader loop switches back to handleEvent
    currentAssistantIdRef.current = bgStream.assistantMsgId;
    streamConversationIdRef.current = cid;
    currentStreamingMsgRef.current = {
      content: bgStream.accumulatedContent,
      toolBadges: bgStream.toolBadges,
      toolRuns: bgStream.toolRuns,
      citationsMap: bgStream.citationsMap,
    };
    // Restore abort controller so handleStop can still kill the stream
    if (bgStream.abortController) {
      abortRef.current = bgStream.abortController;
    }
    setIsStreaming(true);

    // Remove from background — reader loop will see no bgEntry and use handleEvent
    backgroundStreamsRef.current.delete(cid);
    // Also clean up pendingStreamsMap since we're back in foreground
    pendingStreamsMapRef.current.delete(cid);
  }, []);

  // Track the last loaded conversation to prevent unnecessary reloads
  const lastLoadedConversationIdRef = useRef(null);

  // Acting-as context switch: the open conversation's read-only state was
  // computed under the old context. Invalidate and let the load effect rerun.
  const [ownerContextNonce, setOwnerContextNonce] = useState(0);
  const prevReadOwnerRef = useRef(readOwnerUserId ?? null);
  useEffect(() => {
    const prev = prevReadOwnerRef.current;
    prevReadOwnerRef.current = readOwnerUserId ?? null;
    if (String(prev ?? '') === String(readOwnerUserId ?? '')) return;
    const cid = conversationIdRef.current;
    if (!cid) return;
    conversationCacheRef.current.delete(cid);
    lastLoadedConversationIdRef.current = null;
    setOwnerContextNonce((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOwnerUserId]);

  // A deleted chat: drop everything held for it and stop any stream still
  // reading it. MainLayout then opens a new chat if it was the open one.
  useEffect(() => {
    if (!forgetConversationRef) return undefined;
    const forgetConversation = (cid) => {
      if (!cid) return;
      deletedConversationIdsRef.current.add(cid);
      const bgStream = backgroundStreamsRef.current.get(cid);
      // The reader's own finally removes the background entry once it sees the abort.
      try { bgStream?.abortController?.abort(); } catch { /* already closed */ }
      if (streamConversationIdRef.current === cid) {
        try { abortRef.current?.abort(); } catch { /* already closed */ }
        try { reconnectAbortRef.current?.abort(); } catch { /* already closed */ }
      }
      conversationCacheRef.current.delete(cid);
      pendingStreamsMapRef.current.delete(cid);
      if (lastLoadedConversationIdRef.current === cid) lastLoadedConversationIdRef.current = null;
      setPendingStream((current) => (current?.conversationId === cid ? null : current));
      setQueuedDrafts((prev) => (prev.some((d) => d.cid === cid) ? prev.filter((d) => d.cid !== cid) : prev));
    };
    forgetConversationRef.current = forgetConversation;
    return () => {
      if (forgetConversationRef.current === forgetConversation) forgetConversationRef.current = null;
    };
  }, [forgetConversationRef]);

  // Track in-flight conversation load to prevent duplicate concurrent fetches
  const loadingForConversationRef = useRef(null);

  // Load conversation messages when conversationId changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!conversationId) {
        // If we're actively streaming from the welcome screen (conversationId=null,
        // waiting for conversation_started to provide the real ID), don't clear state.
        if (currentAssistantIdRef.current || streamConversationIdRef.current) {
          if (sendingFromWelcomeRef.current) {
            return; // Welcome-screen send — waiting for conversation_started
          }
          // Intentional new-chat while streaming — save state and abort
          detachActiveStream();
        }
        lastLoadedConversationIdRef.current = null;
        loadingForConversationRef.current = null;
        setMessages([]);
        setAdminViewOnly(false);
        setContextUsage(null);
        setConversationFiles([]);
        setFilesDropdownOpen(false);
        setActiveBriefId(null);
        setActiveBrief(null);
        setBriefShareUrl('');
        setBriefOpen(false);
        briefCacheRef.current.clear();
        setIsLoadingConversation(false);
        // Abort any running stream or reconnect when clearing conversation
        abortRef.current?.abort();
        abortRef.current = null;
        reconnectAbortRef.current?.abort();
        reconnectAbortRef.current = null;
        currentAssistantIdRef.current = null;
        streamConversationIdRef.current = null;
        setIsStreaming(false);
        setPendingStream(null);
        processedRedisIdsRef.current.clear();
        return;
      }

      // Check if this is the same conversation that started the stream
      // (the stream sends conversation_started which sets conversationId, triggering this effect)
      // Use BOTH state check AND ref checks - refs are synchronous, state may be stale
      // Check: streamConversationIdRef matches AND (isStreaming state OR reconnecting ref OR active assistant ref)
      // currentAssistantIdRef is set synchronously before streaming starts, making it reliable
      if (streamConversationIdRef.current === conversationId &&
          (isStreaming || isReconnectingRef.current || currentAssistantIdRef.current)) {
        // Same conversation - don't interrupt the stream or reconnect
        console.log('[AtlasChat] loadMessages: skipping - active stream for this conversation');
        return;
      }

      // Skip reload if we already loaded this conversation (prevents refresh after stream ends)
      // This can happen when `conversations` prop changes (e.g., from refreshConversations)
      if (lastLoadedConversationIdRef.current === conversationId) {
        console.log('[AtlasChat] loadMessages: skipping - already loaded');
        return;
      }

      console.warn('[AtlasChat] loadMessages: LOADING conversation', conversationId, 'streamCid=', streamConversationIdRef.current, 'isStreaming=', isStreaming, 'currentAssistantId=', currentAssistantIdRef.current);

      // Also skip if we're in the middle of reconnecting to this conversation
      // This prevents a race where reconnect starts but isStreaming state hasn't updated yet
      if (isReconnectingRef.current && pendingStreamsMapRef.current.has(conversationId)) {
        return;
      }

      // User is switching to a different conversation.
      // Save active stream state and abort HTTP (backend continues via Redis).
      detachActiveStream();

      // Reset brief state; we will rehydrate below if conversation has an active brief.
      setActiveBriefId(null);
      setActiveBrief(null);
      setBriefShareUrl('');
      setBriefPublishError('');
      // Clear brief cache when switching conversations
      briefCacheRef.current.clear();


      // ============== CACHE-AWARE CONVERSATION LOADING ==============

      // Helper: update pagination refs after loading messages
      const updatePaginationState = (msgs, requestedLimit) => {
        if (!msgs || msgs.length === 0) {
          oldestMessageIdRef.current = null;
          setHasMoreMessages(false);
          return;
        }
        // Extract numeric message_id from the id format "hist_{message_id}"
        const firstId = msgs[0]?.id;
        const numericId = firstId ? Number(String(firstId).replace('hist_', '')) : null;
        oldestMessageIdRef.current = Number.isFinite(numericId) ? numericId : null;
        // If we got as many as we asked for, there are likely more
        setHasMoreMessages(msgs.length >= requestedLimit);
      };

      // Check cache first for instant switching
      const cached = getCachedConversation(conversationId);

      if (cached) {
        // INSTANT SWITCH: Use cached data, no loading state
        setMessages(cached.messages);
        setContextUsage(cached.contextUsage || null);
        setConversationFiles(cached.conversationFiles || []);
        setAdminViewOnly(cached.viewerIsOwner === false);
        updatePaginationState(cached.messages, 400);
        lastLoadedConversationIdRef.current = conversationId;
        setIsLoadingConversation(false);

        // Scroll to bottom immediately
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
              top: messagesContainerRef.current.scrollHeight,
              behavior: 'auto'
            });
          }
        }, 10);

        // Background refresh if cache is older than 30 seconds
        const cacheAge = Date.now() - cached.loadedAt;
        if (cacheAge > 30000) {
          // Refresh in background without showing loading state
          const ownerUserId = readOwnerUserIdRef?.current;
          loadConversationData(conversationId, ownerUserId).then((freshData) => {
            if (freshData && lastLoadedConversationIdRef.current === conversationId) {
              // Only update if still viewing this conversation and not streaming
              if (!currentAssistantIdRef.current) {
                setMessages(freshData.messages);
                setContextUsage(freshData.contextUsage || null);
                setConversationFiles(freshData.conversationFiles || []);
                updatePaginationState(freshData.messages, 400);
                cacheConversation(conversationId, freshData);
              }
            }
          });
        }
      } else {
        // NOT IN CACHE: Show loading state and fetch
        // Guard: skip if another run is already fetching this conversation
        if (loadingForConversationRef.current === conversationId) {
          return;
        }
        loadingForConversationRef.current = conversationId;
        setIsLoadingConversation(true);
        setMessages([]);
        setAdminViewOnly(false);
        setContextUsage(null);
        setConversationFiles([]);
        setFilesDropdownOpen(false);
        setHasMoreMessages(false);

        try {
          const ownerUserId = readOwnerUserIdRef?.current;
          const data = await loadConversationData(conversationId, ownerUserId);

          // Stale-check: if streaming started for this conversation during
          // the await, don't overwrite the live messages with historical data.
          if (currentAssistantIdRef.current || streamConversationIdRef.current) {
            setIsLoadingConversation(false);
            loadingForConversationRef.current = null;
            lastLoadedConversationIdRef.current = conversationId;
            // Still cache the data for later use
            if (data) cacheConversation(conversationId, data);
            return;
          }

          // Stale-check: if user switched to a different conversation, discard
          if (conversationIdRef.current !== conversationId) {
            loadingForConversationRef.current = null;
            return;
          }

          if (data) {
            setMessages(data.messages);
            setContextUsage(data.contextUsage || null);
            setConversationFiles(data.conversationFiles || []);
            setAdminViewOnly(data.viewerIsOwner === false);
            updatePaginationState(data.messages, 400);
            cacheConversation(conversationId, data);
          }

          setIsLoadingConversation(false);
          lastLoadedConversationIdRef.current = conversationId;

          // Scroll to bottom after loading
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'auto'
              });
            }
          }, 50);
        } catch (e) {
          setError(e?.message || String(e));
          setIsLoadingConversation(false);
        } finally {
          if (loadingForConversationRef.current === conversationId) {
            loadingForConversationRef.current = null;
          }
        }
      }

      // Rehydrate active brief if available in the conversations list.
      const conv = conversations?.find((c) => c.conversation_id === conversationId);
      if (conv && conv.active_brief_id) {
        setActiveBriefId(conv.active_brief_id);
        setBriefVersions([]);
        setCurrentVersionNumber(null);
        try {
          const ownerUserId = readOwnerUserIdRef?.current;
          // Fetch and cache ALL briefs for this conversation for instant switching
          const { briefs } = await fetchAndCacheAllBriefs({
            conversationId,
            ownerUserId,
            cacheRef: briefCacheRef,
            workspaceContext
          });
          
          // Get the active brief from cache (or fetch if needed)
          let activeBriefData = briefCacheRef.current.get(conv.active_brief_id);
          if (!activeBriefData) {
            const briefRes = await fetchBrief({ conversationId, briefId: conv.active_brief_id, ownerUserId, workspaceContext });
            if (briefRes?.brief) {
              activeBriefData = briefRes.brief;
              briefCacheRef.current.set(conv.active_brief_id, activeBriefData);
            }
          }
          if (activeBriefData) {
            setActiveBrief(activeBriefData);
            // Restore the share link if this brief was already published.
            setBriefShareUrl(activeBriefData.published_share_id ? buildShareUrl(activeBriefData.published_share_id) : '');
          }

          const { versions, currentVersionNumber } = briefsToVersionFormat(briefs, conv.active_brief_id);
          setBriefVersions(versions);
          if (currentVersionNumber) setCurrentVersionNumber(currentVersionNumber);
        } catch {
          // ignore
        }
      }

      // (Research state rehydrates in its own conversationId effect — see the
      // dedicated effect near the research state declarations.)
    };

    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversations, readOwnerUserIdRef, ownerContextNonce, getCachedConversation, loadConversationData, cacheConversation, detachActiveStream]);

  const appendToAssistant = useCallback((delta) => {
    const assistantId = currentAssistantIdRef.current;
    if (!assistantId) {
      console.log('[AtlasChat] appendToAssistant: no assistantId, skipping');
      return;
    }
    setMessages((prev) => {
      const existingIdx = prev.findIndex((m) => m.id === assistantId);

      // If message doesn't exist yet (state update pending), create it now
      if (existingIdx === -1) {
        console.warn('[AtlasChat] appendToAssistant: message NOT found in state, creating new. assistantId=', assistantId, 'messages count=', prev.length, 'assistant msgs=', prev.filter(m => m.role === 'assistant').map(m => m.id));
        const normalized = normalizeAssistantDelta('', delta);
        currentStreamingMsgRef.current.content = normalized;
        return [...prev, {
          id: assistantId,
          role: 'assistant',
          content: normalized,
          finalized: false,
          toolBadges: [],
          toolRuns: {},
          citationsMap: {}
        }];
      }
      
      // Message exists - update it
      const msg = prev[existingIdx];
      const prevContent = msg.content || '';
      const normalized = normalizeAssistantDelta(prevContent, delta);
      const newContent = prevContent + normalized;
      // Also update the ref synchronously for state preservation on switch
      currentStreamingMsgRef.current.content = newContent;
      
      const updated = [...prev];
      updated[existingIdx] = { ...msg, content: newContent };
      return updated;
    });
  }, []);

  const updateAssistantMeta = useCallback((updater) => {
    const assistantId = currentAssistantIdRef.current;
    if (!assistantId) return;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== assistantId) return m;
        const updated = updater(m);
        // Sync tool badges and citations to ref
        if (updated.toolBadges) currentStreamingMsgRef.current.toolBadges = updated.toolBadges;
        if (updated.toolRuns) currentStreamingMsgRef.current.toolRuns = updated.toolRuns;
        if (updated.citationsMap) currentStreamingMsgRef.current.citationsMap = updated.citationsMap;
        return updated;
      })
    );
  }, []);

  const bumpToolBadge = useCallback(
    ({ toolName, toolUseId, toolInput }) => {
      updateAssistantMeta((m) => {
        const badges = Array.isArray(m.toolBadges) ? m.toolBadges : [];
        const runs = m.toolRuns && typeof m.toolRuns === 'object' ? m.toolRuns : {};
        const badgeKey = getToolBadgeKey(toolName, toolInput);
        const presentation = getToolBadgePresentation(toolName, badgeKey);
        const displayLabel = presentation.label || toolName;
        const inputSummary = formatToolBadgeInputSummary(toolName, toolInput);

        const nextRuns = { ...runs };
        if (toolUseId) {
          nextRuns[toolUseId] = {
            toolName,
            badgeKey,
            displayLabel,
            status: 'running',
            startedAt: Date.now(),
            inputSummary
          };
        }

        const existing = badges.find((b) => (b.badgeKey || b.toolName) === badgeKey);
        let nextBadges;
        if (!existing) {
          const recentInputSummaries = inputSummary ? [inputSummary] : [];
          nextBadges = [
            ...badges,
            {
              id: newId(),
              toolName,
              badgeKey,
              displayLabel,
              count: 1,
              runningCount: 1,
              lastStatus: 'running',
              recentInputSummaries,
              lastInputSummary: recentInputSummaries[recentInputSummaries.length - 1] || '',
              createdAt: Date.now()
            }
          ];
        } else {
          nextBadges = badges.map((b) => {
            if ((b.badgeKey || b.toolName) !== badgeKey) return b;
            const priorSummaries = Array.isArray(b.recentInputSummaries)
              ? b.recentInputSummaries.filter((summary) => typeof summary === 'string' && summary.trim())
              : b.lastInputSummary
              ? [b.lastInputSummary]
              : [];
            const recentInputSummaries = inputSummary
              ? [...priorSummaries, inputSummary].slice(-3)
              : priorSummaries.slice(-3);
            return {
              ...b,
              badgeKey,
              displayLabel,
              count: (b.count || 0) + 1,
              runningCount: (b.runningCount || 0) + 1,
              lastStatus: 'running',
              recentInputSummaries,
              lastInputSummary:
                recentInputSummaries[recentInputSummaries.length - 1] || b.lastInputSummary,
              updatedAt: Date.now()
            };
          });
        }

        return { ...m, toolBadges: nextBadges, toolRuns: nextRuns };
      });
    },
    [updateAssistantMeta]
  );

  const finishToolBadge = useCallback(
    ({ toolName, toolUseId, hadError }) => {
      updateAssistantMeta((m) => {
        const badges = Array.isArray(m.toolBadges) ? m.toolBadges : [];
        const runs = m.toolRuns && typeof m.toolRuns === 'object' ? m.toolRuns : {};

        const nextRuns = { ...runs };
        const runMeta = toolUseId ? nextRuns[toolUseId] : null;
        const targetBadgeKey = runMeta?.badgeKey || toolName;
        if (toolUseId && nextRuns[toolUseId]) {
          nextRuns[toolUseId] = { ...nextRuns[toolUseId], status: hadError ? 'error' : 'done' };
        }

        const nextBadges = badges.map((b) => {
          if ((b.badgeKey || b.toolName) !== targetBadgeKey) return b;
          const runningCount = Math.max(0, (b.runningCount || 0) - 1);
          const lastStatus = hadError ? 'error' : runningCount > 0 ? 'running' : 'done';
          return { ...b, runningCount, lastStatus, updatedAt: Date.now() };
        });

        return { ...m, toolBadges: nextBadges, toolRuns: nextRuns };
      });
    },
    [updateAssistantMeta]
  );

  const mergeCitations = useCallback(
    ({ citationMap }) => {
      if (!citationMap || typeof citationMap !== 'object') return;
      updateAssistantMeta((m) => {
        const prevMap = m.citationsMap && typeof m.citationsMap === 'object' ? m.citationsMap : {};
        return { ...m, citationsMap: { ...prevMap, ...citationMap } };
      });
    },
    [updateAssistantMeta]
  );

  const addSavedFile = useCallback(
    (fileInfo) => {
      if (!fileInfo?.file_id) return;
      // If no assistant message is attached right now (conversation re-key
      // window, reconnect gap), buffer the chip instead of dropping it —
      // final_response flushes the buffer onto the finalized message.
      if (!currentAssistantIdRef.current) {
        pendingSavedFilesRef.current.push(fileInfo);
        return;
      }
      updateAssistantMeta((m) => {
        const prevFiles = Array.isArray(m.savedFiles) ? m.savedFiles : [];
        // A corrected re-save supersedes its predecessor — drop the old chip so
        // the user only sees the current version (matches the history-load
        // filter on is_current_version).
        const next = fileInfo.replaces_file_id
          ? prevFiles.filter(f => f.file_id !== fileInfo.replaces_file_id)
          : prevFiles;
        // Avoid duplicates
        if (next.some(f => f.file_id === fileInfo.file_id)) {
          return next === prevFiles ? m : { ...m, savedFiles: next };
        }
        return { ...m, savedFiles: [...next, fileInfo] };
      });
    },
    [updateAssistantMeta]
  );

  const fetchVizScoped = useCallback(async ({ name }) => {
    const cid = conversationIdRef.current;
    if (!cid) {
      const err = new Error('Missing conversationId');
      err.status = 400;
      throw err;
    }
    const ownerUserId = readOwnerUserIdRef?.current;
    return fetchViz({ conversationId: cid, name, ownerUserId });
  }, [readOwnerUserIdRef]);

  const fetchXlsxRangeScoped = useCallback(async ({ xlsxId, sheet, range }) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    return fetchXlsxRange({ xlsxId, sheet, range, ownerUserId });
  }, [readOwnerUserIdRef]);

  const downloadXlsxScoped = useCallback(async ({ xlsxId, filename }) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    return downloadXlsx({ xlsxId, filename, ownerUserId });
  }, [readOwnerUserIdRef]);

  const downloadSavedFileScoped = useCallback(async ({ fileId, filename, file }) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    const fileCompanyId = file?.company_id ?? (
      file?.library_scope_type === 'company' ? file?.library_scope_id : null
    );
    const fileWorkspaceContext = file?.library_scope_type === 'company' && fileCompanyId
      ? { scopeType: 'company', companyId: fileCompanyId, isCompanyScope: true }
      : workspaceContext;
    return downloadSavedFile({ fileId, filename, ownerUserId, workspaceContext: fileWorkspaceContext });
  }, [readOwnerUserIdRef, workspaceContext]);

  const previewSavedFileScoped = useCallback(async ({ fileId, filename, file }) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    const fileCompanyId = file?.company_id ?? (
      file?.library_scope_type === 'company' ? file?.library_scope_id : null
    );
    const fileWorkspaceContext = file?.library_scope_type === 'company' && fileCompanyId
      ? { scopeType: 'company', companyId: fileCompanyId, isCompanyScope: true }
      : workspaceContext;
    return fetchSavedFilePreviewUrl({ fileId, filename, ownerUserId, workspaceContext: fileWorkspaceContext });
  }, [readOwnerUserIdRef, workspaceContext, conversationId, clientUserId]);

  const fileScopeFor = useCallback((file) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    const fileCompanyId = file?.company_id ?? (
      file?.library_scope_type === 'company' ? file?.library_scope_id : null
    );
    if (file?.library_scope_type === 'company' && fileCompanyId) {
      return { ownerUserId, workspaceContext: { scopeType: 'company', companyId: fileCompanyId, isCompanyScope: true } };
    }
    // Saved files resolve in the chat's workspace (as downloadSavedFileScoped
    // does); uploads and the files panel have always resolved by owner alone.
    return { ownerUserId, workspaceContext: file?.viewerOrigin === 'saved' ? workspaceContext : undefined };
  }, [readOwnerUserIdRef, workspaceContext]);

  const loadDocumentFile = useCallback(({ file, variant, signal }) => (
    fetchSavedFileBlob({ fileId: file.file_id, variant, signal, ...fileScopeFor(file) })
  ), [fileScopeFor]);

  const downloadDocumentFile = useCallback((file, blob) => {
    if (blob) {
      saveBlobAs(blob, file.filename);
      return;
    }
    downloadSavedFile({ fileId: file.file_id, filename: file.filename, ...fileScopeFor(file) })
      .catch((err) => console.warn('Download failed', err));
  }, [fileScopeFor]);

  const downloadUploadedFileScoped = useCallback(async ({ fileId, filename }) => {
    const ownerUserId = readOwnerUserIdRef?.current;
    return downloadSavedFile({ fileId, filename, ownerUserId });
  }, [readOwnerUserIdRef]);

  const fetchImageScoped = useCallback(async (imageId) => (
    fetchAtlasImage(imageId, { conversationId: conversationIdRef.current })
  ), []);

  const prefetchViz = useCallback(async (name) => {
    if (!name) return;
    try {
      const cached = vizCacheRef.current.get(name);
      if (cached) return;
      const payload = await fetchVizScoped({ name });
      vizCacheRef.current.set(name, payload);
      if (payload?.type) vizTypeRef.current.set(name, payload.type);
    } catch (e) {
      if (e?.status === 404) return;
      console.warn('Failed to prefetch viz:', e);
    }
  }, [fetchVizScoped]);

  const VizEmbed = useCallback(
    function VizEmbedInner({ name }) {
      const [payload, setPayload] = useState(() => vizCacheRef.current.get(name) || null);
      const [vizErr, setVizErr] = useState(null);

      useEffect(() => {
        let cancelled = false;
        async function load() {
          try {
            const cached = vizCacheRef.current.get(name);
            if (cached) {
              if (!cancelled) setPayload(cached);
              return;
            }
            const maxAttempts = 6;
            let attempt = 0;
            while (attempt < maxAttempts) {
              try {
                const fetched = await fetchVizScoped({ name });
                vizCacheRef.current.set(name, fetched);
                if (fetched?.type) vizTypeRef.current.set(name, fetched.type);
                if (!cancelled) setPayload(fetched);
                return;
              } catch (err) {
                attempt += 1;
                if (err?.status !== 404 || attempt >= maxAttempts) throw err;
                const delayMs = 200 * attempt;
                await new Promise((r) => setTimeout(r, delayMs));
              }
            }
          } catch (e) {
            if (cancelled) return;
            if (e?.status === 404) {
              setVizErr('Visualization not found (the tool may have failed, or the server restarted).');
              return;
            }
            setVizErr(e?.message || String(e));
          }
        }
        if (name) load();
        return () => {
          cancelled = true;
        };
      }, [name]);

      if (!name) return null;
      if (vizErr) return <div className="atlas-viz-error">Failed to load viz `{name}`: {vizErr}</div>;
      if (!payload) {
        const hintType = vizTypeRef.current.get(name);
        const vizClass = hintType === 'map'
          ? 'atlas-viz-map'
          : hintType === 'table'
            ? 'atlas-viz-table'
          : hintType === 'absorption'
            ? 'atlas-viz-absorption'
            : 'atlas-viz-chart';
        return (
          <div className={`atlas-viz ${vizClass} atlas-viz-skeleton`} aria-busy="true">
            <div className="atlas-viz-skeleton-inner">Loading visualization…</div>
          </div>
        );
      }

      if (payload.type === 'absorption') {
        const widgetType = payload.widget_type || payload.widgetType || payload.data?.widget_type || 'single_prediction';
        const widgetData = payload.data || payload;
        return (
          <div className="atlas-viz atlas-viz-absorption">
            <AbsorptionWidget data={widgetData} widgetType={widgetType} />
          </div>
        );
      }

      if (payload.type === 'chart') {
        return <AtlasChartView config={payload.config} variant="chat" />;
      }

      if (payload.type === 'map') {
        return <AtlasMapView config={payload.config} variant="chat" />;
      }

      if (payload.type === 'table') {
        return <AtlasTableViz payload={payload} variant="chat" />;
      }

      return (
        <div className="atlas-viz atlas-viz-raw">
          <pre>{JSON.stringify(payload, null, 2)}</pre>
        </div>
      );
    },
    [fetchVizScoped]
  );

  const ImageEmbed = useCallback(
    function ImageEmbedInner({ imageId }) {
      const [imageUrl, setImageUrl] = useState(null);
      const [imageError, setImageError] = useState(null);
      const [loading, setLoading] = useState(true);

      useEffect(() => {
        let cancelled = false;
        async function loadImage() {
          try {
            let imageObjectUrl;
            try {
              imageObjectUrl = await fetchAtlasImage(imageId, { conversationId: conversationIdRef.current });
            } catch (primaryError) {
              const looksLikeFileId = /^(file|saved|img)_[A-Za-z0-9_-]+$/.test(String(imageId || ''));
              if (!looksLikeFileId || String(imageId || '').startsWith('img_')) {
                throw primaryError;
              }
              imageObjectUrl = await fetchSavedFilePreviewUrl({
                fileId: imageId,
                ownerUserId: readOwnerUserIdRef?.current,
                workspaceContext,
              });
            }
            if (!cancelled) {
              setImageUrl(imageObjectUrl);
              setLoading(false);
            }
          } catch (e) {
            if (!cancelled) {
              if (e?.message?.includes('404')) {
                setImageError('Image not found (may have expired after server restart)');
              } else {
                setImageError(e?.message || String(e));
              }
              setLoading(false);
            }
          }
        }
        loadImage();
        return () => {
          cancelled = true;
        };
      }, [imageId]);

      useEffect(() => {
        return () => {
          if (imageUrl) URL.revokeObjectURL(imageUrl);
        };
      }, [imageUrl]);

      if (!imageId) return null;
      if (imageError) {
        return (
          <div className="atlas-image-error">
            <span className="atlas-image-error-icon">🖼️</span>
            <span>{imageError}</span>
          </div>
        );
      }
      if (loading) {
        return (
          <div className="atlas-image-loading">
            <div className="atlas-image-skeleton">Loading image…</div>
          </div>
        );
      }
      return (
        <div className="atlas-image-container">
          <img src={imageUrl} alt="Generated by Atlas" className="atlas-generated-image" />
        </div>
      );
    },
    [readOwnerUserIdRef, workspaceContext]
  );

  const handleEvent = useCallback(
    async (evt) => {
      if (!evt?.type) return;
      const data = evt.data || {};

      if (evt.type === 'keepalive') return;

      if (evt.type === 'billing_notice') {
        const noticeKey = data.notice_key || `${data.period_start || 'current'}:${data.notice_kind || 'usage'}`;
        if (!seenBillingNoticesRef.current.has(noticeKey)) {
          seenBillingNoticesRef.current.add(noticeKey);
          setBillingNotice(data);
        }
        return;
      }

      // Handle stream_started event - captures message_id for reconnection
      if (evt.type === 'stream_started') {
        const { conversation_id: cid, message_id: mid } = data;
        if (cid && mid) {
          const oldKey = streamConversationIdRef.current;
          streamConversationIdRef.current = cid;

          // Update the pending stream entry with the message_id
          const existingEntry = pendingStreamsMapRef.current.get(oldKey) || pendingStreamsMapRef.current.get(cid);
          if (existingEntry) {
            const updatedEntry = {
              ...existingEntry,
              conversationId: cid,
              messageId: mid,
            };
            // Remove old key if different
            if (oldKey && oldKey !== cid) {
              pendingStreamsMapRef.current.delete(oldKey);
            }
            pendingStreamsMapRef.current.set(cid, updatedEntry);
            setPendingStream(updatedEntry);
          }
        }
        return;
      }

      if (evt.type === 'conversation_started') {
        if (data.conversation_id) {
          sendingFromWelcomeRef.current = false; // Real conversation ID arrived
          const oldConversationId = streamConversationIdRef.current;
          conversationIdRef.current = data.conversation_id;
          streamConversationIdRef.current = data.conversation_id;
          // Mark as "loaded" to prevent unnecessary reload after stream ends
          lastLoadedConversationIdRef.current = data.conversation_id;
          setConversationId(data.conversation_id);
          refreshConversations();

          // Migrate pending stream entry if conversation ID changed (new conversation created)
          // Also capture message_id if provided (for reconnection support)
          const existingEntry = pendingStreamsMapRef.current.get(oldConversationId) ||
                               pendingStreamsMapRef.current.get(data.conversation_id);
          if (existingEntry) {
            const migratedEntry = {
              ...existingEntry,
              conversationId: data.conversation_id,
              // Update messageId if provided in the event
              messageId: data.message_id || existingEntry.messageId,
            };
            // Remove old key if different
            if (oldConversationId && oldConversationId !== data.conversation_id) {
              pendingStreamsMapRef.current.delete(oldConversationId);
            }
            pendingStreamsMapRef.current.set(data.conversation_id, migratedEntry);
            setPendingStream(migratedEntry);
          }
        }
        return;
      }

      if (evt.type === 'context_usage') {
        // Mid-turn meter refresh (e.g. right after compaction frees room) —
        // marked estimated; the exact count at final_response supersedes it.
        const liveUsage = normalizeContextUsage(data.context_usage);
        if (liveUsage) {
          contextUsageRef.current = liveUsage;
          setContextUsage(liveUsage);
        }
        return;
      }

      // Summarized reasoning: its own slot, so it does not clobber (or get
      // clobbered by) tool progress. Intentionally does NOT touch the
      // conversation-id refs — reasoning_update does that, and firing it
      // hundreds of times a turn runs migration logic for no reason.
      if (evt.type === 'reasoning_trace') {
        const trace = (data.message || '').trim();
        if (trace) setReasoningTrace(trace);
        return;
      }

      if (evt.type === 'reasoning_update') {
        if (data.conversation_id) {
          const oldConversationId = streamConversationIdRef.current;
          conversationIdRef.current = data.conversation_id;
          streamConversationIdRef.current = data.conversation_id;
          setConversationId(data.conversation_id);

          // Migrate pending stream entry if conversation ID changed
          if (oldConversationId && oldConversationId !== data.conversation_id) {
            const existingEntry = pendingStreamsMapRef.current.get(oldConversationId);
            if (existingEntry) {
              const migratedEntry = { ...existingEntry, conversationId: data.conversation_id };
              pendingStreamsMapRef.current.delete(oldConversationId);
              pendingStreamsMapRef.current.set(data.conversation_id, migratedEntry);
              setPendingStream(migratedEntry);
            }
          }
        }
        if (typeof data.message === 'string' && data.message.trim()) {
          if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
          setActivity({ kind: 'info', text: data.message.trim(), spinning: true, startedAt: Date.now() });
          setActivityKey((k) => k + 1);
        }
        return;
      }

      if (evt.type === 'text_delta' && typeof data.delta === 'string') {
        appendToAssistant(data.delta);
        if (data.delta.includes('/c/')) preloadLinkedConversations(data.delta);
        return;
      }

      if (evt.type === 'tool_call') {
        const toolName = data.tool_name;
        const toolInput = data.input;
        const toolUseId = data.tool_use_id;
        if (isTransientToolActivity(toolName, data)) {
          if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
          setActivity({
            kind: 'info',
            text: formatTransientToolActivity(toolName),
            spinning: true,
            startedAt: Date.now(),
            toolName
          });
          setActivityKey((k) => k + 1);
          return;
        }
        if (toolName) bumpToolBadge({ toolName, toolUseId, toolInput });
        if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
        const toolUi = toolName ? getToolBadgePresentation(toolName, getToolBadgeKey(toolName, toolInput)) : null;
        setActivity({ 
          kind: 'tool', 
          text: formatToolCall(toolName, toolInput), 
          icon: toolUi?.icon || null,
          spinning: true, 
          startedAt: Date.now(), 
          toolName 
        });
        setActivityKey((k) => k + 1);
        return;
      }

      if (evt.type === 'tool_result') {
        const toolName = data.tool_name;
        const output = data.output;
        const toolUseId = data.tool_use_id;
        if (isTransientToolActivity(toolName, data)) return;
        const hadError =
          (typeof output === 'string' && output.includes('"tool_error"')) ||
          (output && typeof output === 'object' && output.type === 'tool_error') ||
          (output && typeof output === 'object' && output.ok === false);
        if (toolName) finishToolBadge({ toolName, toolUseId, hadError });
        if (toolName === 'web_search' && output?.citation_map) {
          mergeCitations({ citationMap: output.citation_map });
        }
        // Capture saved/returned files for display. Hidden intermediates
        // (generation scripts, PDF render crops) never get user-facing chips.
        // The extractor tolerates string-encoded output so a serialization
        // variant can't silently skip a chip. code_execution results carry
        // sandbox-bridged saves as files_saved — read those too (dedupe makes
        // this idempotent with the backend's synthetic save_file replay).
        const directFile = extractSavedFileFromToolResult(data);
        const capturedFiles = [
          ...(directFile ? [directFile] : []),
          ...extractSavedFilesFromCodeExecution(data),
        ];
        for (const fileInfo of capturedFiles) {
          addSavedFile(fileInfo);
          if (fileInfo.replaces_file_id) {
            // The replaced version may chip on an EARLIER message (it stays
            // visible as conversation history) — flip its badge to superseded.
            setMessages((prev) => prev.map((m) => {
              if (!Array.isArray(m.savedFiles)) return m;
              if (!m.savedFiles.some((f) => f.file_id === fileInfo.replaces_file_id)) return m;
              return {
                ...m,
                savedFiles: m.savedFiles.map((f) =>
                  f.file_id === fileInfo.replaces_file_id
                    ? {
                        ...f,
                        version_badge: {
                          version: f.version_number || f.version_badge?.version || Math.max(1, (fileInfo.version_number || 2) - 1),
                          isLatest: false,
                        },
                      }
                    : f
                ),
              };
            }));
          }
          setConversationFiles((prevFiles) => applySavedFileToList(prevFiles, fileInfo));
        }
        if (toolName && toolName.startsWith('brief_') && output && typeof output === 'object') {
          if (output.ok && output.brief) {
            const b = output.brief;
            if (b?.brief_id) {
              updateAssistantMeta((m) => ({ ...m, briefId: b.brief_id, briefTitle: b?.title || '' }));
              
              // Determine if we should switch/update this brief
              // - brief_get: always switch (user explicitly opened)
              // - brief_create: only open panel if brief has content (blocks), otherwise just track the ID
              // - brief_patch: always open/update if the patched brief has content
              const isGet = toolName === 'brief_get';
              const isCreate = toolName === 'brief_create';
              const isPatch = toolName === 'brief_patch';
              const hasBlocks = Array.isArray(b.blocks) && b.blocks.length > 0;
              
              // Open panel when we have content - for create or patch
              // For patch: if it has blocks, open the panel (the agent just added content)
              const shouldOpenPanel = isGet || (isCreate && hasBlocks) || (isPatch && hasBlocks);
              
              // Always track the active brief ID for brief operations
              setActiveBriefId(b.brief_id);
              setActiveBrief(b);
              // Add to cache for instant switching
              briefCacheRef.current.set(b.brief_id, b);
              
              if (shouldOpenPanel) {
                setResearchOpen(false);
                setBriefOpen(true);
                setBriefPublishError('');
                setBriefShareUrl('');
                // Refresh briefs list (shown as "versions" dropdown)
                const cid = conversationIdRef.current;
                if (cid) {
                  const ownerUserId = readOwnerUserIdRef?.current;
                  fetchConversationBriefs({ conversationId: cid, ownerUserId, workspaceContext })
                    .then((res) => {
                      const { versions, currentVersionNumber } = briefsToVersionFormat(res?.briefs, b.brief_id);
                      setBriefVersions(versions);
                      if (currentVersionNumber) setCurrentVersionNumber(currentVersionNumber);
                    })
                    .catch(() => {});
                }
              }
            }
          }
          if (output.ok && output.published?.share_id) {
            const shareId = output.published.share_id;
            setBriefShareUrl(buildShareUrl(shareId));
            setBriefPublishError('');
          }
          if (output.ok === false && output.error) {
            setBriefPublishError(String(output.error));
          }
        }
        if (
          toolName === 'browser_research_start'
          && output
          && typeof output === 'object'
          && output.ok
          && output.run_id
        ) {
          // Auto-open the live research pane beside the chat (brief-panel
          // pattern). An explicit "watch this" moment: it displaces an open
          // brief; the brief stays one click away via its message chip.
          const runId = output.run_id;
          setResearchRuns((prev) => [
            { run_id: runId, name: output.name || 'Web research', status: output.status || 'queued' },
            ...prev.filter((r) => r.run_id !== runId),
          ]);
          if (!dismissedResearchRunsRef.current.has(runId) && !isMobile) {
            openResearchPanel(runId);
          } else {
            setActiveResearchRunId(runId);
          }
        }
        if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
        setActivity({ kind: hadError ? 'info' : 'success', text: formatToolResult(toolName, output), spinning: false });
        setActivityKey((k) => k + 1);
        activityHideTimerRef.current = setTimeout(() => setActivity(null), 1200);
        return;
      }

      if (evt.type === 'artifact_created') {
        if (data?.artifact_type === 'email_approval' && data?.action_id) {
          // Atlas staged an email send — attach an approval card to this message.
          // A single turn can stage multiple emails, so append (don't overwrite).
          updateAssistantMeta((m) => {
            const prev = Array.isArray(m.emailApprovals) ? m.emailApprovals : [];
            if (prev.some((a) => a.actionId === data.action_id)) return m;
            return {
              ...m,
              emailApprovals: [
                ...prev,
                { actionId: data.action_id, preview: data.preview || {}, status: 'pending' },
              ],
            };
          });
          return;
        }
        if (data?.artifact_type === 'app_action_approval' && data?.action_id) {
          // Atlas staged a change in a connected app (Drive / Docs / Sheets / HubSpot).
          updateAssistantMeta((m) => {
            const prev = Array.isArray(m.appApprovals) ? m.appApprovals : [];
            if (prev.some((a) => a.actionId === data.action_id)) return m;
            return {
              ...m,
              appApprovals: [
                ...prev,
                { actionId: data.action_id, preview: data.preview || {}, status: 'pending' },
              ],
            };
          });
          return;
        }
        if (data?.artifact_type === 'app_connect' && data?.action_id) {
          // Atlas needs an app the user hasn't connected: a Connect card it waits on.
          updateAssistantMeta((m) => {
            const prev = Array.isArray(m.appConnects) ? m.appConnects : [];
            if (prev.some((a) => a.actionId === data.action_id)) return m;
            return {
              ...m,
              appConnects: [...prev, { actionId: data.action_id, preview: data.preview || {}, status: 'pending' }],
            };
          });
          return;
        }
        if (data?.artifact_type === 'brief' && data?.brief_id) {
          // Always track the brief ID, but only open panel if it has content
          // This prevents "No content yet" flash when brief is first created empty
          setActiveBriefId(data.brief_id);
          setBriefPublishError('');
          setBriefShareUrl('');
          setBriefVersions([]);
          setCurrentVersionNumber(null);
          
          const cid = conversationIdRef.current;
          if (cid) {
            try {
              const ownerUserId = readOwnerUserIdRef?.current;
              const [briefRes, allBriefsRes] = await Promise.all([
                fetchBrief({ conversationId: cid, briefId: data.brief_id, ownerUserId, workspaceContext }),
                fetchConversationBriefs({ conversationId: cid, ownerUserId, workspaceContext }).catch(() => ({ briefs: [] }))
              ]);
              if (briefRes?.brief) {
                setActiveBrief(briefRes.brief);
                // Restore the share link if this brief was already published.
                setBriefShareUrl(briefRes.brief.published_share_id ? buildShareUrl(briefRes.brief.published_share_id) : '');
                // Add to cache for instant switching
                briefCacheRef.current.set(data.brief_id, briefRes.brief);
                // Only open brief panel if the brief has content (blocks)
                const hasBlocks = Array.isArray(briefRes.brief.blocks) && briefRes.brief.blocks.length > 0;
                if (hasBlocks && !briefOpen && !researchOpen && !docOpen) {
                  setBriefOpen(true);
                }
              }
              const { versions, currentVersionNumber } = briefsToVersionFormat(allBriefsRes?.briefs, data.brief_id);
              setBriefVersions(versions);
              if (currentVersionNumber) setCurrentVersionNumber(currentVersionNumber);
            } catch {
              // ignore
            }
          }
          return;
        }
        if (data?.artifact_type && data?.name) vizTypeRef.current.set(data.name, data.artifact_type);
        if (data?.name) await prefetchViz(data.name);
        return;
      }

      if (evt.type === 'final_response') {
        const cid = data.conversation_id || conversationIdRef.current || streamConversationIdRef.current;
        const eventContextUsage = normalizeContextUsage(data.context_usage);
        const finalResponseText = typeof data.response === 'string' ? data.response : '';
        if (eventContextUsage) {
          contextUsageRef.current = eventContextUsage;
          setContextUsage(eventContextUsage);
        }
        const contextUsageForCache = eventContextUsage || contextUsageRef.current || null;
        if (data.conversation_id) {
          conversationIdRef.current = data.conversation_id;
          setConversationId(data.conversation_id);
          pendingStreamsMapRef.current.delete(data.conversation_id);
        }
        const assistantId = currentAssistantIdRef.current;
        const pendingFiles = pendingSavedFilesRef.current;
        pendingSavedFilesRef.current = [];
        if (assistantId) {
          setMessages((prev) => {
            const isInterrupted = data.interrupted === true;
            const updated = prev.map((m) => {
              if (m.id !== assistantId) return m;
              const nextContent = (typeof m.content === 'string' && m.content.trim()) ? m.content : finalResponseText;
              let mergedSavedFiles = m.savedFiles;
              for (const fileInfo of pendingFiles) {
                mergedSavedFiles = applySavedFileToList(mergedSavedFiles, fileInfo);
              }
              const finalizedToolBadges = Array.isArray(m.toolBadges)
                ? m.toolBadges.map((badge) => ({
                    ...badge,
                    runningCount: 0,
                    lastStatus:
                      badge.lastStatus === 'error'
                        ? 'error'
                        : isInterrupted || badge.lastStatus === 'interrupted'
                        ? 'interrupted'
                        : 'done',
                  }))
                : m.toolBadges;
              return {
                ...m,
                dbMessageId: data.assistant_message_id || m.dbMessageId || null,
                content: nextContent,
                finalized: true,
                interrupted: isInterrupted || m.interrupted,
                toolBadges: finalizedToolBadges,
                ...(mergedSavedFiles ? { savedFiles: mergedSavedFiles } : {}),
              };
            });
            // Update cache with final messages
            if (cid) {
              const cached = conversationCacheRef.current.get(cid);
              if (cached) {
                cached.messages = updated;
                cached.contextUsage = contextUsageForCache;
                cached.loadedAt = Date.now();
              } else {
                // Create new cache entry
                conversationCacheRef.current.set(cid, {
                  messages: updated,
                  briefsByMessageId: {},
                  savedFiles: [],
                  contextUsage: contextUsageForCache,
                  loadedAt: Date.now()
                });
              }
            }
            return updated;
          });
        }
        if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
        setActivity({ kind: 'success', text: '✅ Done', spinning: false });
        setActivityKey((k) => k + 1);
        activityHideTimerRef.current = setTimeout(() => setActivity(null), 900);
        refreshConversations();
        if (!isNearBottom()) {
          setShowFinishedNotice(true);
        }
        return;
      }

      // Handle stream_incomplete - stream timed out without proper completion
      if (evt.type === 'stream_incomplete') {
        const cid = conversationIdRef.current || streamConversationIdRef.current;
        if (cid) {
          pendingStreamsMapRef.current.delete(cid);
        }
        const assistantId = currentAssistantIdRef.current;
        if (assistantId) {
          // Mark as finalized but incomplete
          setMessages((prev) => prev.map((m) => 
            m.id === assistantId ? { ...m, finalized: true, incomplete: true } : m
          ));
        }
        if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
        setActivity({ kind: 'info', text: '⚠️ Response incomplete', spinning: false });
        setActivityKey((k) => k + 1);
        activityHideTimerRef.current = setTimeout(() => setActivity(null), 3000);
        refreshConversations();
        return;
      }

      if (evt.type === 'error') {
        setError(data.error || 'Unknown error');
        if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
        setActivity({ kind: 'error', text: data.error || 'Error', spinning: false });
        setActivityKey((k) => k + 1);
      }
    },
    [addSavedFile, appendToAssistant, briefOpen, docOpen, bumpToolBadge, finishToolBadge, isMobile, isNearBottom, mergeCitations, openResearchPanel, preloadLinkedConversations, prefetchViz, readOwnerUserIdRef, refreshConversations, researchOpen, setActiveBrief, setActiveBriefId, setBriefOpen, setBriefPublishError, setBriefShareUrl, setBriefVersions, setConversationId, setCurrentVersionNumber, updateAssistantMeta, workspaceContext]
  );

  // Retry failed reconnects with backoff. A transient failure (flaky network,
  // laptop waking from sleep — which is exactly when visibilitychange fires,
  // often before the network is back) must NOT permanently give up on the
  // stream: that left conversations frozen until the run finished and the
  // user reloaded.
  const attemptReconnectRef = useRef(null);
  const reconnectRetryTimerRef = useRef(null);
  const scheduleReconnectRetry = useCallback((streamInfo, attempt) => {
    const MAX_RECONNECT_RETRIES = 4;
    if (!streamInfo?.conversationId || attempt > MAX_RECONNECT_RETRIES) return false;
    const delay = Math.min(1500 * 2 ** (attempt - 1), 12000); // 1.5s → 3s → 6s → 12s
    if (reconnectRetryTimerRef.current) clearTimeout(reconnectRetryTimerRef.current);
    reconnectRetryTimerRef.current = setTimeout(() => {
      reconnectRetryTimerRef.current = null;
      // Hidden tab: skip — the visibilitychange handler retries on return.
      if (document.visibilityState !== 'visible') return;
      attemptReconnectRef.current?.({ ...streamInfo, retryAttempt: attempt });
    }, delay);
    return true;
  }, []);

  useEffect(() => () => {
    if (reconnectRetryTimerRef.current) clearTimeout(reconnectRetryTimerRef.current);
  }, []);

  // Reconnection function - can be called from visibility change or when loading a conversation
  const attemptReconnect = useCallback(
    async (streamInfo) => {
      if (!streamInfo) return false;
      if (isStreaming) return false;
      if (isReconnectingRef.current) return false;

      let { conversationId: cid, messageId: mid, assistantMsgId } = streamInfo;
      if (!cid) return false;
      if (deletedConversationIdsRef.current.has(cid)) return false;
      const retryAttempt = streamInfo.retryAttempt || 0;

      // Set reconnecting flag IMMEDIATELY (synchronously) to prevent race conditions
      // This must happen before any async operations or state updates
      isReconnectingRef.current = true;

      // If we don't have a messageId, try to find an active stream from the backend
      // This handles the case where user navigated away before HTTP headers arrived
      if (!mid) {
        const ownerUserId = readOwnerUserIdRef?.current;
        const activeStream = await findActiveStream({ conversationId: cid, ownerUserId });
        if (activeStream?.message_id) {
          mid = activeStream.message_id;
          // Only update messageId, keep the existing assistantMsgId to avoid duplicates
          const updatedEntry = { ...streamInfo, messageId: mid };
          pendingStreamsMapRef.current.set(cid, updatedEntry);
          setPendingStream(updatedEntry);
        } else {
          // Null can mean "stream finished and expired" OR "couldn't reach
          // the backend" — retry before treating it as gone.
          isReconnectingRef.current = false;
          if (scheduleReconnectRetry(streamInfo, retryAttempt + 1)) {
            return false;
          }
          if (streamInfo.accumulatedContent && assistantMsgId) {
            // Restore whatever content we accumulated before navigating away
            setMessages((prev) => {
              const existingIdx = prev.findIndex((m) => m.id === assistantMsgId);
              const restoredMsg = {
                id: assistantMsgId,
                role: 'assistant',
                content: streamInfo.accumulatedContent || '',
                finalized: true, // Mark as finalized since we can't continue
                toolBadges: streamInfo.toolBadges || [],
                toolRuns: streamInfo.toolRuns || {},
                citationsMap: streamInfo.citationsMap || {},
                incomplete: true // Flag that this message may be incomplete
              };
              if (existingIdx >= 0) {
                const updated = [...prev];
                updated[existingIdx] = { ...updated[existingIdx], ...restoredMsg };
                return updated;
              }
              return [...prev, restoredMsg];
            });
          }
          // Clean up
          pendingStreamsMapRef.current.delete(cid);
          setPendingStream(null);
          isReconnectingRef.current = false; // Reset since we're not reconnecting
          return false;
        }
      }

      try {
        // checkStreamStatus returns null for ANY failure (network error,
        // 5xx) — that is "couldn't check", not "stream gone". Only a
        // definitive exists:false may permanently drop the pending stream;
        // anything else gets retried with backoff.
        const status = await checkStreamStatus({ conversationId: cid, messageId: mid });

        if (status === null || status === undefined) {
          isReconnectingRef.current = false;
          if (scheduleReconnectRetry({ ...streamInfo, messageId: mid }, retryAttempt + 1)) {
            return false;
          }
          // Out of retries — give up like before.
          setPendingStream(null);
          pendingStreamsMapRef.current.delete(cid);
          processedRedisIdsRef.current.clear();
          return false;
        }

        if (!status.exists) {
          setPendingStream(null);
          pendingStreamsMapRef.current.delete(cid);
          processedRedisIdsRef.current.clear();
          isReconnectingRef.current = false;
          return false;
        }

        // Stream exists - check if it's already complete
        if (status.is_complete) {
          // Stream completed while we were away - still reconnect to get final events
        }

        // CACHE-AWARE RECONNECTION:
        // First, ensure we have the conversation history loaded (from cache or fetch)
        // Then add/update the streaming message on top of it
        const cached = conversationCacheRef.current.get(cid);
        let baseMessages = [];
        
        if (cached && cached.messages.length > 0) {
          // Use cached history as base
          baseMessages = cached.messages;
        } else {
          // Fallback: try to fetch history in background
          // For now, use current messages if available
          // The reconnect will still work, but history may be incomplete
        }

        // IMPORTANT: When reconnecting, we replay ALL events from Redis starting from '0'.
        // This means text_delta events will rebuild the COMPLETE content from scratch.
        // We MUST start with EMPTY content to avoid duplication.
        // Clear processedRedisIdsRef to allow all events to be processed fresh.
        processedRedisIdsRef.current.clear();
        
        // Also reset the streaming message ref since we're starting fresh
        currentStreamingMsgRef.current = {
          content: '',
          toolBadges: [],
          toolRuns: {},
          citationsMap: {}
        };

        // Add the placeholder message if it doesn't exist, or update if it does
        setMessages((prev) => {
          // If current messages are empty but we have cached history, use that as base
          const currentMsgs = prev.length > 0 ? prev : baseMessages;
          
          const existingIdx = currentMsgs.findIndex((m) => m.id === assistantMsgId);
          if (existingIdx >= 0) {
            // Update existing message - CLEAR content since we're replaying ALL events from '0'
            // The stream will send all text_delta events to rebuild the complete content
            const existingMsg = currentMsgs[existingIdx];
            const updated = [...currentMsgs];
            updated[existingIdx] = {
              ...existingMsg,
              // MUST be empty - stream replay will rebuild everything
              content: '',
              finalized: false, // Mark as streaming since we're reconnecting
              // Clear tool state - will be rebuilt from stream
              toolBadges: [],
              toolRuns: {},
              citationsMap: {}
            };
            return updated;
          }
          // Message doesn't exist - add empty streaming placeholder to the end of history
          return [...currentMsgs, {
            id: assistantMsgId,
            role: 'assistant',
            content: '', // Empty - stream will fill it
            finalized: false,
            toolBadges: [],
            toolRuns: {},
            citationsMap: {}
          }];
        });

        setIsStreaming(true);
        currentAssistantIdRef.current = assistantMsgId;
        streamConversationIdRef.current = cid;
        setPendingStream(streamInfo);
        setActivity({ kind: 'info', text: '🔄 Reconnecting…', spinning: true, startedAt: Date.now() });
        setActivityKey((k) => k + 1);

        // Create abort controller for this reconnect
        const reconnectController = new AbortController();
        reconnectAbortRef.current = reconnectController;

        const res = await openResumeStream({
          conversationId: cid,
          messageId: mid,
          lastRedisId: '0',
          signal: reconnectController.signal
        });

        if (!res.body) throw new Error('resume stream missing body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        const myStreamCid = cid; // local alias for background mode checks

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          buf += chunk;
          const lines = buf.split('\n');
          buf = lines.pop() || '';

          // Replay performance: reconnects replay the whole stream from 0, so
          // a long run arrives as chunks with hundreds of events. Adjacent
          // text_delta events in a chunk are coalesced into one handleEvent
          // call, and the pendingStream state (a re-render per update) is
          // written once per chunk — the map still records every redis_id.
          let pendingDelta = null;
          const flushDelta = async () => {
            if (!pendingDelta) return;
            const merged = { ...pendingDelta.evt, data: { ...pendingDelta.evt.data, delta: pendingDelta.delta } };
            pendingDelta = null;
            await handleEvent(merged);
          };
          let lastRedisIdInChunk = null;

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            let evt;
            try {
              evt = JSON.parse(trimmed);
            } catch {
              console.warn('[AtlasChat] Failed to parse line:', trimmed);
              continue;
            }
            if (evt.redis_id && processedRedisIdsRef.current.has(evt.redis_id)) {
              continue;
            }

            // Check if this stream has been moved to background
            const bgEntry = backgroundStreamsRef.current.get(myStreamCid);
            if (bgEntry && !bgEntry.completed) {
              await flushDelta();
              accumulateBackgroundEvent(evt, bgEntry, myStreamCid, conversationCacheRef);
              continue;
            }

            if (evt.redis_id) {
              processedRedisIdsRef.current.add(evt.redis_id);
              lastRedisIdInChunk = evt.redis_id;
              // Update the map as well - use mid (which may have been discovered) not streamInfo.messageId
              const currentEntry = pendingStreamsMapRef.current.get(cid) || streamInfo;
              pendingStreamsMapRef.current.set(cid, { ...currentEntry, messageId: mid, lastRedisId: evt.redis_id });
            }

            if (evt.type === 'text_delta' && typeof evt.data?.delta === 'string') {
              if (pendingDelta) {
                pendingDelta.delta += evt.data.delta;
              } else {
                pendingDelta = { evt, delta: evt.data.delta };
              }
              continue;
            }

            await flushDelta();
            await handleEvent(evt);
          }

          await flushDelta();
          if (lastRedisIdInChunk) {
            setPendingStream((prev) => (prev ? { ...prev, lastRedisId: lastRedisIdInChunk } : null));
          }
        }

        // Stream completed successfully
        // Only update foreground state if we're NOT in background mode.
        // If backgrounded, accumulateBackgroundEvent already updated the cache
        // correctly — touching setMessages here would save the WRONG conversation's
        // messages under this conversation's cache key.
        const completedInBg = backgroundStreamsRef.current.has(cid);
        if (!completedInBg) {
          setPendingStream(null);
          processedRedisIdsRef.current.clear();

          // Update cache with final messages
          setMessages((currentMessages) => {
            if (currentMessages.length > 0 && cid) {
              conversationCacheRef.current.set(cid, {
                messages: currentMessages,
                briefsByMessageId: {},
                savedFiles: [],
                contextUsage: contextUsageRef.current || null,
                loadedAt: Date.now()
              });
            }
            return currentMessages;
          });
        }
        pendingStreamsMapRef.current.delete(cid);

        return true;
      } catch (err) {
        if (err.name === 'AbortError') {
          // User switched away or stopped - state is already saved
          return false;
        }

        // Check if this stream was running in background when it errored
        const bgEntryCatch = backgroundStreamsRef.current.get(cid);
        if (bgEntryCatch) {
          bgEntryCatch.completed = true;
          const cached = conversationCacheRef.current.get(cid);
          if (cached) {
            const errorMessages = cached.messages.map((m) => {
              if (m.id !== bgEntryCatch.assistantMsgId) return m;
              let savedFiles = m.savedFiles;
              for (const fileInfo of bgEntryCatch.savedFiles || []) savedFiles = applySavedFileToList(savedFiles, fileInfo);
              return { ...m, content: bgEntryCatch.accumulatedContent, finalized: true, incomplete: true,
                    toolBadges: bgEntryCatch.toolBadges.map((b) => ({ ...b, runningCount: 0, lastStatus: 'done' })),
                    ...(savedFiles ? { savedFiles } : {}) };
            });
            conversationCacheRef.current.set(cid, { ...cached, messages: errorMessages, loadedAt: Date.now() });
          }
          return false;
        }

        console.warn('Reconnection failed:', err);

        // Transient failure mid-reconnect (dropped connection while reading
        // the resume stream): keep the pending entry and retry — replay
        // restarts from 0, so no state is lost.
        if (scheduleReconnectRetry({ ...streamInfo, messageId: mid }, retryAttempt + 1)) {
          pendingStreamsMapRef.current.set(cid, { ...streamInfo, messageId: mid });
          processedRedisIdsRef.current.clear();
          return false;
        }

        // Out of retries — clean up on error
        setPendingStream(null);
        pendingStreamsMapRef.current.delete(cid);
        processedRedisIdsRef.current.clear();

        return false;
      } finally {
        // Check if this stream completed/errored while in background
        const bgEntry = backgroundStreamsRef.current.get(cid);
        if (bgEntry) {
          backgroundStreamsRef.current.delete(cid);
          isReconnectingRef.current = false;
          // DON'T touch foreground refs
        } else {
          reconnectAbortRef.current = null;
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamConversationIdRef.current = null;
          isReconnectingRef.current = false;
          if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
          setActivity(null);
          setReasoningTrace('');
          // Refresh conversation files panel (new files may have been created)
          const refreshCid = conversationIdRef.current;
          if (refreshCid) {
            fetchConversationFiles({ conversationId: refreshCid, ownerUserId: readOwnerUserIdRef?.current, workspaceContext })
              .then(files => { if (conversationIdRef.current === refreshCid) setConversationFiles(files); })
              .catch(() => {});
          }
        }
      }
    },
    [isStreaming, handleEvent, readOwnerUserIdRef, scheduleReconnectRetry, workspaceContext]
  );

  // Keep the retry timer pointed at the latest reconnect closure
  useEffect(() => {
    attemptReconnectRef.current = attemptReconnect;
  }, [attemptReconnect]);

  // Visibility change handler for reconnection
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!pendingStream) return;
      await attemptReconnect(pendingStream);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [pendingStream, attemptReconnect]);

  // Check for pending streams when loading a conversation (e.g., user switched away and back, or page refresh)
  useEffect(() => {
    if (!conversationId) return;
    if (isStreaming) return;
    if (isReconnectingRef.current) return;
    // Skip reconnection for conversations the user explicitly stopped
    if (stoppedConversationsRef.current.has(conversationId)) return;
    // Wait until messages have loaded before checking for active streams
    // This ensures we can properly match and update existing messages
    if (isLoadingConversation) return;

    // Check for background stream first (instant, no Redis reconnection needed)
    const bgStream = backgroundStreamsRef.current.get(conversationId);
    if (bgStream) {
      if (bgStream.completed) {
        // Stream finished in background — cache already updated by accumulateBackgroundEvent
        backgroundStreamsRef.current.delete(conversationId);
        pendingStreamsMapRef.current.delete(conversationId);
        return; // loadMessages already restored from cache
      }
      // Still running — promote to foreground (instant)
      promoteBackgroundStream(conversationId, bgStream);
      return;
    }

    // First try direct lookup by conversation ID (in-memory)
    let savedPending = pendingStreamsMapRef.current.get(conversationId);

    // If not found, search for entries where the conversationId field matches
    // This handles cases where entry was stored under pending_${assistantMsgId} key
    // but the conversationId field was updated when we received the real ID
    if (!savedPending) {
      for (const [key, entry] of pendingStreamsMapRef.current.entries()) {
        if (entry.conversationId === conversationId) {
          savedPending = entry;
          if (key !== conversationId) {
            pendingStreamsMapRef.current.delete(key);
            pendingStreamsMapRef.current.set(conversationId, entry);
          }
          break;
        }
      }
    }

    if (savedPending) {
      // CRITICAL: Reuse the EXACT same assistantMsgId that was saved
      // This ensures we find and update the existing message in the cache,
      // rather than creating a duplicate with a different ID
      // Only generate a new ID if we truly don't have one (page refresh case is handled below)
      const assistantMsgIdToUse = savedPending.assistantMsgId || `reconnect_${savedPending.messageId || conversationId}_${Date.now()}`;
      
      // Save back if we generated a new one
      if (!savedPending.assistantMsgId) {
        const updatedPending = { ...savedPending, assistantMsgId: assistantMsgIdToUse };
        pendingStreamsMapRef.current.set(conversationId, updatedPending);
      }
      
      attemptReconnect({
        ...savedPending,
        assistantMsgId: assistantMsgIdToUse
      });
    } else {
      // No local pending stream - check server for active streams
      // This handles page refresh: Redis stream may still be active even though JS state is lost
      (async () => {
        try {
          const ownerUserId = readOwnerUserIdRef?.current;
          const activeStream = await findActiveStream({ conversationId, ownerUserId });
          // Orphaned Redis streams can outlive newer, completed turns. Do not
          // replay them over saved history when the producer is known to be gone.
          // Unknown liveness still permits reconnecting after a transient check failure.
          if (activeStream?.message_id && !activeStream?.is_complete && activeStream.producer_alive !== false) {
            // Use reconnect_ prefix since Redis message_id (UUID) differs from DB message_id (integer)
            // The assistant message doesn't exist in DB until stream completes, so we create a new one
            const assistantMsgId = `reconnect_${activeStream.message_id}`;
            const streamInfo = {
              conversationId,
              messageId: activeStream.message_id,
              assistantMsgId,
              lastRedisId: '0',
              accumulatedContent: ''
            };
            pendingStreamsMapRef.current.set(conversationId, streamInfo);
            attemptReconnect(streamInfo);
          }
        } catch (err) {
          console.warn('[AtlasChat] Failed to check server for active streams:', err);
        }
      })();
    }
  }, [conversationId, isStreaming, isLoadingConversation, attemptReconnect, promoteBackgroundStream, readOwnerUserIdRef]);

  const handleSend = useCallback(
    async (text, { usageOverageOverride = false, skipUsageLimitCheck = false } = {}) => {
      setError(null);
      setFilesDropdownOpen(false);

      const msg = (text || '').trim();
      if (!msg) return;
      // A steer is mid-flight: the old turn is stopping and the send gate has
      // not cleared. Sending now would lose the race and surface a 409, so
      // hold this message and let it go out after the steer's turn.
      if (steerPendingRef.current) {
        handleQueueRef.current?.(msg);
        return;
      }
      if (promptForCompanyConversationConfirm()) {
        // The composer has already cleared: hold the message and send it on Proceed.
        pendingCompanyMessageRef.current = { text: msg, options: { usageOverageOverride, skipUsageLimitCheck } };
        return;
      }
      const actAsUserId = shouldWriteAsSelectedClient ? dataUserId : null;
      if (actAsUserId != null) {
        const approved = window.confirm("This will write on a client's profile. Approve?");
        if (!approved) return;
      }
      if (!ATLAS_AGENT_API_URL) {
        setError('REACT_APP_ATLAS_AGENT_API_URL is not set');
        return;
      }

      // Capture attached files for display in the user message
      // Use temporary pending IDs initially; real file_ids are patched in after upload
      const filesForUserMsg = attachedFiles.map(f => ({
        file_id: f.id,
        pending_id: f.id,
        filename: f.name,
        size_bytes: f.size,
        mime_type: f.mimeType,
        ...(f.kind ? { kind: f.kind, request_id: f.requestId } : {}),
        // Company library files open in the company scope (see fileScopeFor).
        ...(f.status === 'existing' && f.workspaceScopeType === 'company' && f.companyId
          ? { library_scope_type: 'company', company_id: f.companyId }
          : {}),
      }));
      
      const userMsg = {
        id: newId(),
        role: 'user',
        content: msg,
        createdAt: Date.now(),
        uploadedFiles: filesForUserMsg,  // Show attached files above message
        finalized: true,
      };
      const assistantMsgId = newId();
      const assistantMsg = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        finalized: false,
        toolBadges: [],
        toolRuns: {},
        citationsMap: {}
      };
      currentAssistantIdRef.current = assistantMsgId;
      
      // Reset the streaming message ref for the new message
      currentStreamingMsgRef.current = {
        content: '',
        toolBadges: [],
        toolRuns: {},
        citationsMap: {}
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setShowFinishedNotice(false);
      setActivity({ kind: 'info', text: '🧠 Atlas is working…', spinning: true, startedAt: Date.now() });
      setActivityKey((k) => k + 1);

      setTimeout(scrollToBottom, 50);

      const controller = new AbortController();
      abortRef.current = controller;

      // Pre-register the stream in pendingStreamsMap IMMEDIATELY (before fetch)
      // This ensures we can save state even if user navigates away before headers arrive
      const targetConversationId = conversationId || `pending_${assistantMsgId}`;
      sendingFromWelcomeRef.current = !conversationId; // True when sending from welcome screen
      streamConversationIdRef.current = targetConversationId;
      // A stop marks a conversation "do not reconnect" so we don't resurrect
      // the stream the user just killed. Starting a NEW turn ends that: without
      // clearing it, the conversation could never promote a background stream
      // or reconnect again for the rest of the session — one stop (or one
      // steer, which stops) froze it permanently.
      stoppedConversationsRef.current.delete(targetConversationId);
      if (conversationId) stoppedConversationsRef.current.delete(conversationId);
      const earlyPendingData = {
        conversationId: targetConversationId,
        messageId: null, // Will be populated once headers arrive
        assistantMsgId: assistantMsgId,
        lastRedisId: '0',
        accumulatedContent: '',
        toolBadges: [],
        toolRuns: {},
        citationsMap: {}
      };
      pendingStreamsMapRef.current.set(targetConversationId, earlyPendingData);
      setPendingStream(earlyPendingData);

      // Capture pending files and existing files before clearing
      const pendingFiles = attachedFiles.filter(f => f.status === 'pending' && f.file);
      const existingFiles = attachedFiles.filter(f => f.status === 'existing' && f.existingFileId);
      const referenceAttachments = attachedFiles.filter(f => f.status === 'reference' && f.kind && f.requestId);

      // Clear attached files after capturing
      setAttachedFiles([]);
      setFileUploadError(null);

      let myStreamCid = targetConversationId; // tracks this stream's conversation ID across background transitions
      let streamFinished = false; // final event seen: stop reading, and a later socket error is moot
      try {
        // The chat endpoint checks the usage cap before it creates a
        // conversation or stores anything, and a blocked send comes back as a
        // 402 that the catch below rolls back. Only uploads need a preflight,
        // so they don't store files for a turn that never runs. It runs after
        // the optimistic render so the user's message never waits on it.
        if (pendingFiles.length > 0 && !usageOverageOverride && !skipUsageLimitCheck) {
          let usageLimit = null;
          try {
            usageLimit = await fetchUsageLimitStatus(workspaceContext);
          } catch (usageLimitError) {
            // Fail open: the chat endpoint remains authoritative.
            console.warn('[AtlasChat] Usage limit preflight failed:', usageLimitError);
          }
          if (usageLimit?.blocked) {
            const usageLimitErr = new Error('Your company has reached its Atlas usage limit.');
            usageLimitErr.usageLimit = usageLimit;
            throw usageLimitErr;
          }
        }

        const idToken = await getIdToken();
        
        // Upload pending files first (if any)
        const uploadedFileIds = [];
        // What happened to each attachment, keyed by its composer id (the
        // chip's pending_id): { file_id } or { error }.
        const attachmentOutcomes = {};
        const attachmentFailures = [];

        if (pendingFiles.length > 0) {
          setActivity({ kind: 'info', text: `📎 Uploading ${pendingFiles.length} file(s)…`, spinning: true, startedAt: Date.now() });
          
          for (const pendingFile of pendingFiles) {
            try {
              const result = await uploadFile({
                conversationId: targetConversationId,
                file: pendingFile.file,
                actAsUserId,
                workspaceContext,
              });
              uploadedFileIds.push(result.file_id);
              attachmentOutcomes[pendingFile.id] = { file_id: result.file_id };
            } catch (uploadErr) {
              console.error('[AtlasChat] File upload failed:', uploadErr);
              const reason = uploadErr?.message || 'upload failed';
              attachmentOutcomes[pendingFile.id] = { error: reason };
              attachmentFailures.push(`Failed to upload ${pendingFile.name}: ${reason}`);
              // Continue with other files
            }
          }
          
          setActivity({ kind: 'info', text: '🧠 Atlas is working…', spinning: true, startedAt: Date.now() });
        }

        // Attach existing files from File Browser (already in blob storage).
        // Company files need their workspace or the backend looks in the
        // personal library and 404s.
        if (existingFiles.length > 0) {
          for (const ef of existingFiles) {
            try {
              const result = await attachFileToConversation({
                conversationId: targetConversationId,
                fileId: ef.existingFileId,
                workspaceContext: ef.workspaceScopeType === 'company' && ef.companyId
                  ? { scopeType: 'company', companyId: ef.companyId }
                  : null,
              });
              const linkedId = result?.file_id || ef.existingFileId;
              uploadedFileIds.push(linkedId);
              attachmentOutcomes[ef.id] = { file_id: linkedId };
            } catch (attachErr) {
              console.error('[AtlasChat] Attach existing file failed:', attachErr);
              const reason = attachErr?.message || 'attach failed';
              attachmentOutcomes[ef.id] = { error: reason };
              attachmentFailures.push(`Couldn't attach ${ef.name}: ${reason}`);
            }
          }
        }

        // Patch real file_ids (or a failure) onto the chip each one belongs to.
        if (Object.keys(attachmentOutcomes).length > 0) {
          setMessages(prev => prev.map(m => (
            m.id !== userMsg.id || !m.uploadedFiles
              ? m
              : { ...m, uploadedFiles: applyAttachmentOutcomes(m.uploadedFiles, attachmentOutcomes) }
          )));
        }
        if (attachmentFailures.length > 0) {
          setFileUploadError(attachmentFailures.join(' · '));
        }

        // Use targetConversationId to match the ID used for file uploads
        const payload = applyWorkspaceScopeToPayload({ conversation_id: targetConversationId, message: msg }, workspaceContext);
        if (workspaceContext?.isCompanyScope) {
          payload.include_personal_memories_in_company_mode = Boolean(
            atlasUserPreferences?.use_personal_memories_in_company_mode
          );
        }
        if (usageOverageOverride) {
          payload.usage_overage_override = true;
        }
        if (atlasMcpConnector?.enabled && atlasMcpConnector?.url) {
          payload.mcp_servers = [{
            type: 'url',
            name: 'user-mcp',
            url: atlasMcpConnector.url,
          }];
        }
        // Include uploaded file IDs
        if (uploadedFileIds.length > 0) {
          payload.file_ids = uploadedFileIds;
        }
        if (referenceAttachments.length > 0) {
          payload.attachments = referenceAttachments.map(f => ({ kind: f.kind, request_id: f.requestId, address: f.address || null }));
        }
        const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        if (!res.ok) {
          if (res.status === 402) {
            const body = await res.json().catch(() => ({}));
            const usageLimitErr = new Error(body?.detail || 'Your company has reached its Atlas usage limit.');
            usageLimitErr.usageLimit = body?.usage_limit || { blocked: true, can_override: false };
            throw usageLimitErr;
          }
          if (res.status === 409) {
            // Backend gates concurrent turns per conversation (turn_in_progress).
            let detail =
              'Atlas is still working on the previous message in this conversation. Stop it with the stop button or wait for it to finish, then send again.';
            try {
              const body = await res.json();
              if (body?.detail) detail = body.detail;
            } catch {
              // keep default detail
            }
            const conflictErr = new Error(detail);
            conflictErr.turnInProgress = true;
            throw conflictErr;
          }
          throw new Error(`stream failed: ${res.status}`);
        }
        if (!res.body) throw new Error('stream missing body');

        const streamMessageId = res.headers.get('X-Message-Id');
        const streamConversationIdFromHeader = res.headers.get('X-Conversation-Id') || conversationId;

        // Check if stream was detached to background while we were awaiting fetch.
        // This happens when user switches conversations before the HTTP response arrives.
        const detachedDuringFetch = backgroundStreamsRef.current.has(targetConversationId);

        if (detachedDuringFetch) {
          // Stream was moved to background — migrate the background entry to the real conversation ID
          // so the reader loop can find it. DO NOT re-set streamConversationIdRef (keep it null).
          if (targetConversationId !== streamConversationIdFromHeader) {
            const bgEntry = backgroundStreamsRef.current.get(targetConversationId);
            backgroundStreamsRef.current.delete(targetConversationId);
            backgroundStreamsRef.current.set(streamConversationIdFromHeader, bgEntry);
            // Also migrate the pendingStreamsMap entry
            const pendingEntry = pendingStreamsMapRef.current.get(targetConversationId);
            if (pendingEntry) {
              pendingStreamsMapRef.current.delete(targetConversationId);
              pendingStreamsMapRef.current.set(streamConversationIdFromHeader, {
                ...pendingEntry,
                conversationId: streamConversationIdFromHeader,
                messageId: streamMessageId || pendingEntry.messageId,
              });
            }
          }
          // Still generate title and refresh sidebar so new conversation appears
          if (sendingFromWelcomeRef.current) {
            const titleFilenames = filesForUserMsg.length > 0 ? filesForUserMsg.map(f => f.filename) : undefined;
            generateConversationTitle({
              conversationId: streamConversationIdFromHeader,
              message: msg,
              filenames: titleFilenames,
              ownerUserId: readOwnerUserIdRef?.current,
            })
              .then(() => refreshConversations())
              .catch(() => {});
          }
        } else {
          // Normal case: stream still in foreground — update ref to real conversation ID
          streamConversationIdRef.current = streamConversationIdFromHeader;
        }

        if (streamMessageId && !detachedDuringFetch) {
          // Get the existing pre-registered entry and update it with the messageId
          const existingPre = pendingStreamsMapRef.current.get(targetConversationId);
          const pendingData = {
            conversationId: streamConversationIdFromHeader,
            messageId: streamMessageId,
            assistantMsgId: assistantMsgId,
            lastRedisId: existingPre?.lastRedisId || '0',
            // Preserve any accumulated content from the pre-registration
            accumulatedContent: existingPre?.accumulatedContent || '',
            toolBadges: existingPre?.toolBadges || [],
            toolRuns: existingPre?.toolRuns || {},
            citationsMap: existingPre?.citationsMap || {}
          };
          setPendingStream(pendingData);

          // If conversation ID changed (e.g., new conversation created), migrate the entry
          if (targetConversationId !== streamConversationIdFromHeader) {
            pendingStreamsMapRef.current.delete(targetConversationId);
          }

          // Refresh conversation files panel now that we have the real conversation ID
          // (files were uploaded under pending ID and migrated by the backend)
          if (uploadedFileIds.length > 0) {
            fetchConversationFiles({ conversationId: streamConversationIdFromHeader, ownerUserId: readOwnerUserIdRef?.current, workspaceContext })
              .then(files => { if (conversationIdRef.current === streamConversationIdFromHeader) setConversationFiles(files); })
              .catch(() => {});
          }

          pendingStreamsMapRef.current.set(streamConversationIdFromHeader, pendingData);
          processedRedisIdsRef.current.clear();

          // Generate a Haiku title for new conversations (fire-and-forget)
          if (sendingFromWelcomeRef.current) {
            const titleFilenames = filesForUserMsg.length > 0 ? filesForUserMsg.map(f => f.filename) : undefined;
            generateConversationTitle({
              conversationId: streamConversationIdFromHeader,
              message: msg,
              filenames: titleFilenames,
              ownerUserId: readOwnerUserIdRef?.current,
            })
              .then(() => refreshConversations())
              .catch(err => console.warn('[AtlasChat] Title generation failed (non-fatal):', err));
          }
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        // Capture local stream CID — survives ref clears when detached to background
        myStreamCid = streamConversationIdFromHeader || conversationId;

        while (!streamFinished) {
          let chunk;
          try {
            chunk = await reader.read();
          } catch (readErr) {
            // A body read failure (Chrome: "TypeError: network error") is a
            // dropped connection, not a failed run — tagged so the catch below
            // resumes from Redis instead of showing the browser's message.
            if (readErr && typeof readErr === 'object') readErr.streamRead = true;
            throw readErr;
          }
          const { value, done } = chunk;
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            let evt;
            try {
              evt = JSON.parse(trimmed);
            } catch (parseErr) {
              console.warn('Bad NDJSON line:', trimmed);
              continue;
            }
            if (evt.type === 'final_response' || evt.type === 'stream_incomplete') {
              streamFinished = true;
            }

            // Check if this stream has been moved to background
            const bgEntry = backgroundStreamsRef.current.get(myStreamCid);
            if (bgEntry && !bgEntry.completed) {
              accumulateBackgroundEvent(evt, bgEntry, myStreamCid, conversationCacheRef);
              continue;
            }

            // Track conversation_started updating myStreamCid
            if (evt.type === 'conversation_started' && evt.data?.conversation_id) {
              myStreamCid = evt.data.conversation_id;
            }

            // Normal foreground processing
            if (evt.redis_id) {
              processedRedisIdsRef.current.add(evt.redis_id);
              setPendingStream((prev) => {
                if (!prev) return null;
                const updated = { ...prev, lastRedisId: evt.redis_id };
                // Keep map in sync
                pendingStreamsMapRef.current.set(prev.conversationId, updated);
                return updated;
              });
            }
            await handleEvent(evt);
          }
        }
        if (streamFinished) {
          // The run is over; don't wait on the socket to close cleanly.
          reader.cancel().catch(() => {});
        }
        // Stream completed - clean up
        // Clean up THIS stream's entry, not whatever conversation happens to be
        // in the foreground now. streamConversationIdRef is global and moves
        // when the user switches conversations, so a background stream
        // finishing here used to delete the foreground conversation's pending
        // entry — after which its Stop button could not find a message id and
        // silently skipped the backend cancel.
        pendingStreamsMapRef.current.delete(myStreamCid);
        if (!backgroundStreamsRef.current.get(myStreamCid)) {
          setPendingStream(null);
        }
      } catch (err) {
        if (err?.usageLimit) {
          pendingStreamsMapRef.current.delete(targetConversationId);
          setMessages((currentMessages) => currentMessages.filter(
            (message) => message.id !== userMsg.id && message.id !== assistantMsgId
          ));
          setAttachedFiles(attachedFiles);
          setUsageLimitPrompt({ message: msg, status: err.usageLimit });
          setPendingStream(null);
          sendingFromWelcomeRef.current = false;
          return;
        }
        if (err?.name === 'AbortError') {
          // User aborted (stop button or handleStop) - don't clear state, it's saved
          return;
        }

        // Check if this stream was running in background when it errored
        const bgEntry = backgroundStreamsRef.current.get(myStreamCid);
        if (bgEntry) {
          bgEntry.completed = true;
          const cached = conversationCacheRef.current.get(myStreamCid);
          if (cached) {
            const errorMessages = cached.messages.map((m) => {
              if (m.id !== bgEntry.assistantMsgId) return m;
              let savedFiles = m.savedFiles;
              for (const fileInfo of bgEntry.savedFiles || []) savedFiles = applySavedFileToList(savedFiles, fileInfo);
              return { ...m, content: bgEntry.accumulatedContent, finalized: true, incomplete: true,
                    toolBadges: bgEntry.toolBadges.map((b) => ({ ...b, runningCount: 0, lastStatus: 'done' })),
                    ...(savedFiles ? { savedFiles } : {}) };
            });
            conversationCacheRef.current.set(myStreamCid, { ...cached, messages: errorMessages, loadedAt: Date.now() });
          }
          return; // let finally handle cleanup
        }

        // Connection dropped mid-run (sleep, network change, proxy reset). The
        // run carries on server-side and every event is in Redis, so resume
        // instead of leaving a half-rendered answer under a bare "network
        // error" (conv 4c5b0543). finally resets the foreground refs; the
        // reconnect re-arms them and replays the stream from the start.
        const resumeEntry = err?.streamRead && !streamFinished
          ? pendingStreamsMapRef.current.get(myStreamCid) || pendingStreamsMapRef.current.get(targetConversationId)
          : null;
        if (resumeEntry && scheduleReconnectRetry(resumeEntry, 1)) {
          console.warn('[AtlasChat] Stream dropped mid-run; resuming:', err?.message || err);
          return;
        }

        // Normal foreground error handling
        setError(err?.message || String(err));
        const errorConversationId = streamConversationIdRef.current;
        if (errorConversationId) {
          pendingStreamsMapRef.current.delete(errorConversationId);

          // Update cache with current messages even on error so user doesn't lose content
          setMessages((currentMessages) => {
            if (currentMessages.length > 0) {
              // Mark the streaming message as incomplete but finalized
              const updatedMsgs = currentMessages.map((m) =>
                m.id === currentAssistantIdRef.current
                  ? { ...m, finalized: true, incomplete: true }
                  : m
              );
              conversationCacheRef.current.set(errorConversationId, {
                messages: updatedMsgs,
                briefsByMessageId: {},
                savedFiles: [],
                contextUsage: contextUsageRef.current || null,
                loadedAt: Date.now()
              });
              return updatedMsgs;
            }
            return currentMessages;
          });
        }
        setPendingStream(null);
      } finally {
        // Check if this stream completed/errored while in background
        const bgEntry = backgroundStreamsRef.current.get(myStreamCid);
        if (bgEntry) {
          // Stream ended in background — cache was already updated
          backgroundStreamsRef.current.delete(myStreamCid);
          // DON'T touch foreground refs — they belong to a different conversation
        } else {
          // Normal foreground cleanup
          setIsStreaming(false);
          abortRef.current = null;
          currentAssistantIdRef.current = null;
          streamConversationIdRef.current = null;
          processedRedisIdsRef.current.clear();
          if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
          setActivity(null);
          setReasoningTrace('');
          // Refresh conversation files panel (new files may have been created)
          const refreshCid = conversationIdRef.current;
          if (refreshCid) {
            fetchConversationFiles({ conversationId: refreshCid, ownerUserId: readOwnerUserIdRef?.current, workspaceContext })
              .then(files => { if (conversationIdRef.current === refreshCid) setConversationFiles(files); })
              .catch(() => {});
          }
        }
      }
    },
    [attachedFiles, atlasMcpConnector?.enabled, atlasMcpConnector?.url, atlasUserPreferences?.use_personal_memories_in_company_mode, conversationId, dataUserId, handleEvent, promptForCompanyConversationConfirm, readOwnerUserIdRef, refreshConversations, scheduleReconnectRetry, scrollToBottom, shouldWriteAsSelectedClient, workspaceContext]
  );

  // Proceed on the company-conversation prompt sends the message that was waiting for it.
  useEffect(() => {
    if (!companyConversationConfirmed || companyConversationConfirmOpen) return;
    const pending = pendingCompanyMessageRef.current;
    if (!pending) return;
    pendingCompanyMessageRef.current = null;
    handleSend(pending.text, pending.options);
  }, [companyConversationConfirmed, companyConversationConfirmOpen, handleSend]);

  // A card decided after Atlas stopped waiting on it (its turn already ended) picks the
  // turn back up, so the user doesn't have to type. Only the latest reply's cards count,
  // only once none of them is still open (a batch of emails continues once, after the
  // last one), and only if something went ahead: all declined -> nothing to carry on.
  // While Atlas is still running, the turn itself sees the decision.
  const cardMessagesRef = useRef(messages);
  cardMessagesRef.current = messages;
  const cardStreamingRef = useRef(isStreaming);
  cardStreamingRef.current = isStreaming;
  const continuedAfterCardsRef = useRef(new Set());
  const onCardDecided = useCallback((decision = {}) => {
    const { actionId, status } = decision;
    if (!actionId || !status || status === 'pending') return;
    setMessages((prev) => prev.map((m) => withCardStatus(m, actionId, status)));
    const next = continuationAfterCard(cardMessagesRef.current, decision, {
      streaming: cardStreamingRef.current,
      alreadyContinued: (id) => continuedAfterCardsRef.current.has(id),
    });
    if (!next) return;
    continuedAfterCardsRef.current.add(next.messageId);
    handleSend(next.text);
  }, [handleSend]);

  const keepEditingUsageLimitedMessage = useCallback(() => {
    const pendingMessage = usageLimitPrompt?.message || '';
    setUsageLimitPrompt(null);
    if (pendingMessage) inputAreaRef.current?.setInputText(pendingMessage);
  }, [usageLimitPrompt]);

  const submitUrgentUsageOverride = useCallback(() => {
    const pendingMessage = usageLimitPrompt?.message || '';
    if (!pendingMessage || !usageLimitPrompt?.status?.can_override) return;
    setUsageLimitPrompt(null);
    handleSend(pendingMessage, { usageOverageOverride: true, skipUsageLimitCheck: true });
  }, [handleSend, usageLimitPrompt]);

  const usageLimitModalNode = usageLimitPrompt ? (
    <div className="atlas-usage-limit-overlay" role="presentation">
      <div
        className="atlas-usage-limit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-usage-limit-title"
      >
        <div className="atlas-usage-limit-eyebrow">Usage limit reached</div>
        <h2 id="atlas-usage-limit-title" className="atlas-usage-limit-title">
          This message hasn&apos;t been sent
        </h2>
        <p className="atlas-usage-limit-copy">
          Your company has reached its Atlas usage limit for this billing cycle. To stop seeing this prompt, an owner or admin can raise the overage limit in Settings → Billing & Usage.
        </p>
        {usageLimitPrompt.status?.can_override && (
          <div className="atlas-usage-limit-callout">
            Would you like to continue? Usage from this request will be added to your company&apos;s monthly bill above the current limit.
          </div>
        )}
        <div className="atlas-usage-limit-actions">
          <button type="button" className="atlas-usage-limit-secondary" onClick={keepEditingUsageLimitedMessage}>
            Cancel
          </button>
          {usageLimitPrompt.status?.can_override && (
            <button type="button" className="atlas-usage-limit-primary" onClick={submitUrgentUsageOverride}>
              Continue with billable usage
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const handleStop = useCallback(async ({ keepQueuedDrafts = false } = {}) => {
    // 1. Capture IDs needed for the backend cancel call
    const cid = streamConversationIdRef.current;
    const pending = cid ? pendingStreamsMapRef.current.get(cid) : null;
    const mid = pending?.messageId;

    // Stopping is not "the turn finished" — hold the queue until a new turn
    // is genuinely running (see suppressQueueFlushRef).
    suppressQueueFlushRef.current = true;
    if (!keepQueuedDrafts && cid) {
      // An explicit stop strands any queued text: there is no turn left to
      // wait for. Put it back in the composer instead of dropping it silently
      // or leaving a chip that never sends.
      setQueuedDrafts((prev) => {
        const mine = prev.filter((d) => d.cid === cid);
        if (mine.length > 0) {
          const restored = mine.map((d) => d.text).join('\n\n');
          requestAnimationFrame(() => inputAreaRef.current?.setInputText(restored));
        }
        return prev.filter((d) => d.cid !== cid);
      });
    }

    // 2. Abort the HTTP stream immediately (instant UI responsiveness)
    abortRef.current?.abort();
    abortRef.current = null;
    reconnectAbortRef.current?.abort();
    reconnectAbortRef.current = null;

    // 3. Finalize the current assistant message with partial content
    //    and mark any still-running tool badges as done (stops rainbow spin)
    const assistantId = currentAssistantIdRef.current;
    if (assistantId) {
      setMessages((prev) => prev.map((m) => {
        if (m.id !== assistantId) return m;
        const stoppedBadges = Array.isArray(m.toolBadges)
          ? m.toolBadges.map((b) =>
              (b.runningCount || 0) > 0
                ? { ...b, runningCount: 0, lastStatus: 'interrupted' }
                : b
            )
          : [];
        return { ...m, finalized: true, interrupted: true, toolBadges: stoppedBadges };
      }));
    }

    // 4. Clear streaming state to re-enable the input area
    currentAssistantIdRef.current = null;
    streamConversationIdRef.current = null;
    setIsStreaming(false);
    setPendingStream(null);
    if (cid) {
      pendingStreamsMapRef.current.delete(cid);
      stoppedConversationsRef.current.add(cid);
    }
    processedRedisIdsRef.current.clear();
    if (activityHideTimerRef.current) clearTimeout(activityHideTimerRef.current);
    setActivity(null);
    setReasoningTrace('');

    // 5. Fire-and-forget backend cancel to stop the agent task (saves API costs)
    if (cid && mid) {
      try {
        await cancelStream({ conversationId: cid, messageId: mid });
      } catch (err) {
        console.warn('[AtlasChat] Failed to cancel backend task:', err);
      }
    }
  }, []);

  // --- Mid-turn input: steer (inject into the running turn) and queue ---

  const handleQueue = useCallback((text) => {
    const cid = conversationIdRef.current || streamConversationIdRef.current;
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    if (!cid) return;
    setQueuedDrafts((prev) => [...prev, { id: newId(), cid, text: trimmed }]);
    // The queued bubble renders at the end of the transcript, so from a
    // scrolled-up position queueing looks like the message vanished — the
    // composer clears and nothing visibly happens. Same 50ms beat handleSend
    // uses, to let the new node land before scrolling.
    setTimeout(scrollToBottom, 50);
  }, [scrollToBottom]);

  const handleRemoveQueuedDraft = useCallback((draftId) => {
    setQueuedDrafts((prev) => prev.filter((d) => d.id !== draftId));
  }, []);

  useEffect(() => {
    handleQueueRef.current = handleQueue;
  }, [handleQueue]);

  // "Send now" on a queued message: stop the running turn and start on this
  // one instead. Steering is deliberate and per-message rather than the
  // default Enter behavior, because unlike Codex's soft steer ours cancels
  // the in-flight response.
  const handleSendQueuedNow = useCallback((draftId) => {
    const draft = queuedDrafts.find((d) => d.id === draftId);
    if (!draft) return;
    setQueuedDrafts((prev) => prev.filter((d) => d.id !== draftId));
    handleSteerRef.current?.(draft.text);
  }, [queuedDrafts]);

  // Steer = hard interrupt: stop the running turn, then send the new prompt as
  // its own turn so it gets its own bubble. The backend keeps the interrupted
  // turn in its snapshot and tells the model it was cut short, so the work so
  // far carries over as context rather than being thrown away. Rendering order
  // stays chronological (partial answer → new prompt → new answer), which the
  // previous mid-turn injection could not do.
  const handleSteer = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    const cid = streamConversationIdRef.current || conversationIdRef.current;
    if (!cid || !isStreaming) {
      handleSend(trimmed);
      return;
    }
    // Queued drafts survive a steer — they still mean "after the current
    // response", and the steer is what the current response becomes.
    // Feedback for the stop-then-restart gap. This deliberately does not use
    // setActivity: that status line only renders against an UNFINALIZED
    // assistant message whose id matches currentAssistantId, and handleStop
    // finalizes the message and clears that ref — so it would never appear.
    // The ref also gates handleSend, so a manual send during the gap is
    // queued instead of racing this steer's own send into the 409 turn gate.
    steerPendingRef.current = true;
    setSteerPending(true);
    try {
      // Queued drafts survive a steer — they still mean "after the current
      // response", and the steer is what the current response becomes.
      await handleStop({ keepQueuedDrafts: true });
      // The send gate rejects a new turn while the previous one is winding
      // down. The cancel endpoint already awaits the agent task; poll the
      // active-stream check to close the remaining gap rather than racing it.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const active = await findActiveStream({
          conversationId: cid,
          ownerUserId: readOwnerUserIdRef?.current,
        });
        if (!active?.message_id) break;
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    } finally {
      steerPendingRef.current = false;
      setSteerPending(false);
    }
    handleSend(trimmed);
  }, [handleSend, handleStop, isStreaming, readOwnerUserIdRef]);

  useEffect(() => {
    handleSteerRef.current = handleSteer;
  }, [handleSteer]);

  // Flush queued drafts once no stream is running for the active conversation.
  // Covers natural completion, errors, stop, and switching back to a
  // conversation whose turn ended while the user was elsewhere. Combined into
  // one send so multiple drafts don't chain multiple turns.
  useEffect(() => {
    // A new turn is genuinely running: whatever stopped the last one is done
    // being special, so queued drafts may flush when THIS turn ends.
    if (isStreaming) suppressQueueFlushRef.current = false;
  }, [isStreaming]);

  useEffect(() => {
    if (isStreaming) return;
    if (suppressQueueFlushRef.current) return;
    if (!conversationId) return;
    // isStreaming is the RENDERED value and lags a stream that is being
    // promoted from background in this same commit (the reconnect effect
    // declared above calls setIsStreaming(true), which this closure cannot
    // see). Consult the refs so switching back to a still-running
    // conversation doesn't flush a draft into a second concurrent turn.
    if (backgroundStreamsRef.current.has(conversationId)) return;
    if (streamConversationIdRef.current === conversationId) return;
    const mine = queuedDrafts.filter((d) => d.cid === conversationId);
    if (mine.length === 0) return;
    setQueuedDrafts((prev) => prev.filter((d) => d.cid !== conversationId));
    handleSend(mine.map((d) => d.text).join('\n\n'));
  }, [isStreaming, conversationId, queuedDrafts, handleSend]);

  const handleDividerMouseDown = useCallback(
    (e) => {
      // The divider serves whichever side panel holds the slot (brief, research or document).
      if (!briefOpen && !researchOpen && !docOpen) return;
      if (briefFullscreen || researchFullscreen || docFullscreen) return;
      splitDragRef.current.dragging = true;
      splitDragRef.current.startX = e.clientX;
      splitDragRef.current.startWidth = briefWidth;
      document.body.classList.add('atlas-resizing');
      e.preventDefault();
    },
    [briefOpen, briefFullscreen, briefWidth, researchOpen, researchFullscreen, docOpen, docFullscreen]
  );

  const handleMenuPromptSelect = useCallback((prompt) => {
    inputAreaRef.current?.setInputText(prompt);
  }, []);

  const handleCopyMessage = useCallback(async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content || '');
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.warn('Failed to copy:', err);
    }
  }, []);

  const handleRollbackMessage = useCallback(async (messageId, msgIdx) => {
    if (!conversationId || !messageId) return;
    try {
      const result = await rollbackConversation({ conversationId, messageId });
      if (result?.ok) {
        setMessages(prev => prev.slice(0, msgIdx + 1));
      }
    } catch (err) {
      console.error('Rollback failed:', err);
      alert(`Rollback failed: ${err.message}`);
    }
  }, [conversationId]);

  const handleLoadToolTraceDetails = useCallback(async (message) => {
    if (!user?.isAdmin || !conversationId || !message?.dbMessageId) return [];
    const ownerUserId = readOwnerUserIdRef?.current;
    const data = await fetchConversationToolTraces({
      conversationId,
      messageId: message.dbMessageId,
      ownerUserId,
    });
    const traces = Array.isArray(data?.traces) ? data.traces : [];
    setMessages(prev => prev.map((m) => (
      m.id === message.id
        ? { ...m, toolTraces: traces, toolTraceDetailsLoaded: true }
        : m
    )));
    const cached = conversationCacheRef.current.get(conversationId);
    if (cached) {
      cached.messages = cached.messages.map((m) => (
        m.id === message.id
          ? { ...m, toolTraces: traces, toolTraceDetailsLoaded: true }
          : m
      ));
      cached.loadedAt = Date.now();
    }
    return traces;
  }, [conversationId, readOwnerUserIdRef, user?.isAdmin]);

  const handleOpenSnapshotViewer = useCallback(async () => {
    if (!user?.isAdmin || !conversationId) return;
    const ownerUserId = readOwnerUserIdRef?.current;
    setSnapshotViewer({ open: true, loading: true, error: '', data: null });
    try {
      const data = await fetchConversationSnapshot({ conversationId, ownerUserId });
      setSnapshotViewer({ open: true, loading: false, error: '', data });
    } catch (err) {
      setSnapshotViewer({
        open: true,
        loading: false,
        error: err?.message || String(err),
        data: null,
      });
    }
  }, [conversationId, readOwnerUserIdRef, user?.isAdmin]);

  const handleCloseSnapshotViewer = useCallback(() => {
    setSnapshotViewer({ open: false, loading: false, error: '', data: null });
  }, []);

  const handleOpenRuntimeDebugViewer = useCallback(async () => {
    if (!user?.isAdmin || !conversationId) return;
    const ownerUserId = readOwnerUserIdRef?.current;
    setRuntimeDebugViewer({ open: true, loading: true, error: '', data: null });
    try {
      const data = await fetchConversationRuntimeDebug({ conversationId, ownerUserId });
      setRuntimeDebugViewer({ open: true, loading: false, error: '', data });
    } catch (err) {
      setRuntimeDebugViewer({
        open: true,
        loading: false,
        error: err?.message || String(err),
        data: null,
      });
    }
  }, [conversationId, readOwnerUserIdRef, user?.isAdmin]);

  const handleCloseRuntimeDebugViewer = useCallback(() => {
    setRuntimeDebugViewer({ open: false, loading: false, error: '', data: null });
  }, []);

  const snapshotJson = useMemo(() => {
    if (!snapshotViewer.data) return '';
    return JSON.stringify(snapshotViewer.data, null, 2);
  }, [snapshotViewer.data]);

  const runtimeDebugJson = useMemo(() => {
    if (!runtimeDebugViewer.data) return '';
    return JSON.stringify(runtimeDebugViewer.data, null, 2);
  }, [runtimeDebugViewer.data]);

  const snapshotModalNode = snapshotViewer.open ? (
    <div className="atlas-snapshot-modal-overlay" role="presentation" onClick={handleCloseSnapshotViewer}>
      <div
        className="atlas-snapshot-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-snapshot-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="atlas-snapshot-modal-header">
          <div>
            <div className="atlas-snapshot-modal-eyebrow">Admin</div>
            <h2 id="atlas-snapshot-modal-title" className="atlas-snapshot-modal-title">Conversation Snapshot</h2>
          </div>
          <button
            type="button"
            className="atlas-snapshot-modal-close"
            onClick={handleCloseSnapshotViewer}
            aria-label="Close snapshot"
          >
            <IoCloseOutline size={20} />
          </button>
        </div>
        <pre className="atlas-snapshot-json">
          {snapshotViewer.loading ? 'Loading snapshot...' : snapshotViewer.error || snapshotJson}
        </pre>
      </div>
    </div>
  ) : null;

  const runtimeDebugData = runtimeDebugViewer.data || {};
  const runtimeCacheSummary = runtimeDebugData.cache_summary || {};
  const runtimeUsageEvents = Array.isArray(runtimeDebugData.usage_events) ? runtimeDebugData.usage_events : [];
  const runtimeCompactionEvents = Array.isArray(runtimeDebugData.compaction_events) ? runtimeDebugData.compaction_events : [];
  const runtimeTimeline = Array.isArray(runtimeDebugData.timeline) ? runtimeDebugData.timeline : [];

  const runtimeDebugModalNode = runtimeDebugViewer.open ? (
    <div className="atlas-snapshot-modal-overlay" role="presentation" onClick={handleCloseRuntimeDebugViewer}>
      <div
        className="atlas-snapshot-modal atlas-runtime-debug-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="atlas-runtime-debug-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="atlas-snapshot-modal-header">
          <div>
            <div className="atlas-snapshot-modal-eyebrow">Admin Debug</div>
            <h2 id="atlas-runtime-debug-modal-title" className="atlas-snapshot-modal-title">Runtime Cache And Compaction</h2>
          </div>
          <button
            type="button"
            className="atlas-snapshot-modal-close"
            onClick={handleCloseRuntimeDebugViewer}
            aria-label="Close runtime debug"
          >
            <IoCloseOutline size={20} />
          </button>
        </div>
        <div className="atlas-runtime-debug-body">
          {runtimeDebugViewer.loading && (
            <div className="atlas-runtime-debug-empty">Loading runtime timeline...</div>
          )}
          {!runtimeDebugViewer.loading && runtimeDebugViewer.error && (
            <div className="atlas-runtime-debug-error">{runtimeDebugViewer.error}</div>
          )}
          {!runtimeDebugViewer.loading && !runtimeDebugViewer.error && (
            <>
              <div className="atlas-runtime-debug-summary" aria-label="Cache summary">
                <div>
                  <span>Prompt</span>
                  <strong>{formatTokenCompact(runtimeCacheSummary.prompt_tokens)} tokens</strong>
                </div>
                <div>
                  <span>Cache Read</span>
                  <strong>{formatTokenCompact(runtimeCacheSummary.cache_read_tokens)} ({formatDebugPercent(runtimeCacheSummary.cache_read_pct)})</strong>
                </div>
                <div>
                  <span>Cache Write</span>
                  <strong>{formatTokenCompact(runtimeCacheSummary.cache_write_tokens)} ({formatDebugPercent(runtimeCacheSummary.cache_write_pct)})</strong>
                </div>
                <div>
                  <span>Output</span>
                  <strong>{formatTokenCompact(runtimeCacheSummary.output_tokens)} tokens</strong>
                </div>
                <div>
                  <span>API Calls</span>
                  <strong>{runtimeCacheSummary.api_call_count || 0}</strong>
                </div>
              </div>

              <section className="atlas-runtime-debug-section">
                <div className="atlas-runtime-debug-section-header">
                  <h3>Turn Timeline</h3>
                  <span>{runtimeTimeline.length} events</span>
                </div>
                {runtimeTimeline.length === 0 ? (
                  <div className="atlas-runtime-debug-empty">No runtime events recorded yet.</div>
                ) : (
                  <div className="atlas-runtime-debug-timeline">
                    {runtimeTimeline.map((event, idx) => (
                      <div key={`${event.type}-${event.created_at || idx}-${idx}`} className={`atlas-runtime-debug-timeline-row is-${event.type}`}>
                        <span className="atlas-runtime-debug-time">{formatDebugDate(event.created_at)}</span>
                        <span className="atlas-runtime-debug-kind">{event.type === 'compaction' ? 'Compaction' : 'Model Call'}</span>
                        <span className="atlas-runtime-debug-label">{event.label || event.status || event.type}</span>
                        <span className="atlas-runtime-debug-metric">
                          {event.type === 'compaction'
                            ? `${formatDebugPercent(event.reduction_pct)} smaller`
                            : `${formatDebugPercent(event.cache_read_pct)} cache read`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="atlas-runtime-debug-section">
                <div className="atlas-runtime-debug-section-header">
                  <h3>Cache Behavior By API Call</h3>
                  <span>{runtimeUsageEvents.length} calls</span>
                </div>
                <div className="atlas-runtime-debug-table-wrap">
                  <table className="atlas-runtime-debug-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Call</th>
                        <th>Status</th>
                        <th>Input</th>
                        <th>Read</th>
                        <th>Write</th>
                        <th>Output</th>
                        <th>Read %</th>
                        <th>Prompt</th>
                        <th>Tools</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runtimeUsageEvents.map((event) => (
                        <tr key={event.usage_event_id || `${event.created_at}-${event.api_call_index}`}>
                          <td>{formatDebugDate(event.created_at)}</td>
                          <td>{event.api_call_index}</td>
                          <td><span className={`atlas-runtime-cache-status is-${event.cache_status}`}>{event.cache_status}</span></td>
                          <td>{formatTokenCompact(event.input_tokens)}</td>
                          <td>{formatTokenCompact(event.cache_read_tokens)}</td>
                          <td>{formatTokenCompact(event.cache_write_tokens)}</td>
                          <td>{formatTokenCompact(event.output_tokens)}</td>
                          <td>{formatDebugPercent(event.cache_read_pct)}</td>
                          <td>{formatTokenCompact(event.prompt_tokens)}</td>
                          <td>{event.context?.tools_count ?? '—'}</td>
                        </tr>
                      ))}
                      {runtimeUsageEvents.length === 0 && (
                        <tr><td colSpan="10">No model usage events recorded.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="atlas-runtime-debug-section">
                <div className="atlas-runtime-debug-section-header">
                  <h3>Compaction Events</h3>
                  <span>{runtimeCompactionEvents.length} events</span>
                </div>
                <div className="atlas-runtime-debug-table-wrap">
                  <table className="atlas-runtime-debug-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Kind</th>
                        <th>Before</th>
                        <th>After</th>
                        <th>Reduction</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runtimeCompactionEvents.map((event) => (
                        <tr key={event.compaction_event_id || `${event.created_at}-${event.kind}`}>
                          <td>{formatDebugDate(event.created_at)}</td>
                          <td>{event.kind}</td>
                          <td>{event.before_chars == null ? '—' : formatDebugChars(event.before_chars)}</td>
                          <td>{event.after_chars == null ? '—' : formatDebugChars(event.after_chars)}</td>
                          <td>{formatDebugPercent(event.reduction_pct)}</td>
                          <td>
                            <details>
                              <summary>JSON</summary>
                              <pre>{JSON.stringify(event.summary || {}, null, 2)}</pre>
                            </details>
                          </td>
                        </tr>
                      ))}
                      {runtimeCompactionEvents.length === 0 && (
                        <tr><td colSpan="6">No compaction events recorded.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <details className="atlas-runtime-debug-raw">
                <summary>Raw debug payload</summary>
                <pre>{runtimeDebugJson}</pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <CardDecisionContext.Provider value={onCardDecided}>
    <div className={[
      'atlas-chat-container',
      (briefOpen || ((researchOpen || docOpen) && !isMobile)) && !briefFullscreen && !(researchOpen && researchFullscreen) && !(docOpen && docFullscreen) ? 'atlas-chat-brief-open' : '',
      briefFullscreen || (researchOpen && researchFullscreen && !isMobile) || (docOpen && docFullscreen && !isMobile) ? 'atlas-chat-brief-fullscreen' : '',
      isMobile ? 'atlas-chat-mobile' : '',
      isWelcomeState ? 'atlas-chat-welcome' : '',
      workspaceContext?.isCompanyScope ? 'atlas-chat-company-mode' : '',
      hasClientContext ? 'atlas-chat-admin-client-mode' : '',
      showMobileBriefFullscreen || (isMobile && researchOpen && activeResearchRunId) || (isMobile && docOpen) ? 'atlas-chat-mobile-brief-fullscreen' : ''
    ].filter(Boolean).join(' ')}>
      <div className="atlas-chat-split">
        {/* Main chat area - hidden on mobile when brief is fullscreen */}
        {!showMobileBriefFullscreen && (
        <div 
          className={`atlas-chat-main ${isDragOver ? 'atlas-chat-drag-over' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragOver && (
            <div className="atlas-drag-overlay">
              <div className="atlas-drag-overlay-content">
                <IoCloudUploadOutline size={48} />
                <span>Drop files to upload</span>
                <span className="atlas-drag-hint">PDF, images, CSV, Excel, and more</span>
              </div>
            </div>
          )}
          {/* Chat Header */}
          <div className="atlas-chat-header">
            <div className="atlas-chat-header-left">
              {/* Hamburger menu for mobile */}
              {isMobile && (
                <button
                  type="button"
                  className="atlas-mobile-menu-btn"
                  onClick={onMenuClick}
                  aria-label="Open menu"
                >
                  <IoMenuOutline size={24} />
                </button>
              )}
              <span className="atlas-chat-header-title">Atlas</span>
              {workspaceContext?.isCompanyScope && (
                <span className="atlas-workspace-badge" title="Company workspace">
                  {workspaceContext.companyName || 'Company'}
                </span>
              )}
            </div>
            <div className="atlas-chat-header-right">
              {messages.length > 0 && contextUsage && (
                <AtlasContextMeter contextUsage={contextUsage} />
              )}
              {user?.isAdmin && conversationId && (
                <>
                  <button
                    type="button"
                    className="atlas-header-admin-btn"
                    onClick={handleOpenRuntimeDebugViewer}
                    title="View cache and compaction runtime timeline"
                  >
                    <IoStatsChartOutline size={16} />
                    <span>Runtime</span>
                  </button>
                  <button
                    type="button"
                    className="atlas-header-admin-btn"
                    onClick={handleOpenSnapshotViewer}
                    title="View full conversation snapshot"
                  >
                    <IoCodeSlashOutline size={16} />
                    <span>Snapshot</span>
                  </button>
                </>
              )}
              {/* Conversation files badge + dropdown */}
              {conversationId && (
                <ConversationFilesPanel
                  files={conversationFiles}
                  isOpen={filesDropdownOpen}
                  onToggle={() => setFilesDropdownOpen(prev => !prev)}
                  onDownload={({ fileId, filename }) => {
                    const ownerUserId = readOwnerUserIdRef?.current;
                    downloadSavedFile({ fileId, filename, ownerUserId });
                  }}
                  onOpen={(file) => {
                    setFilesDropdownOpen(false);
                    openDocument({ ...file, viewerOrigin: 'files_panel' });
                  }}
                />
              )}
              {/* See Brief button - shown when there's an active brief and brief panel is not open */}
              {activeBriefId && !briefOpen && (
                <button
                  className="atlas-header-brief-btn"
                  type="button"
                  title="See Brief"
                  onClick={() => { setResearchOpen(false); setDocOpen(false); handleOpenBriefMobile(activeBriefId); }}
                >
                  <IoDocumentOutline size={16} />
                  <span>See Brief</span>
                </button>
              )}
              {/* Viewer button - reopens the document viewer with its open tabs */}
              {docTabs.length > 0 && !docOpen && (
                <button
                  className="atlas-header-brief-btn"
                  type="button"
                  title="Reopen the document viewer"
                  onClick={() => openDocument(docTabs.find((d) => d.file_id === activeDocId) || docTabs[docTabs.length - 1])}
                >
                  <IoDocumentsOutline size={16} />
                  <span>{docTabs.length === 1 ? 'Document' : `Documents (${docTabs.length})`}</span>
                </button>
              )}
              {/* Web Research button - reopens the research panel for this
                  conversation's run (live or finished) */}
              {activeResearchRunId && !researchOpen && (
                <button
                  className="atlas-header-brief-btn"
                  type="button"
                  title="Web Research"
                  onClick={() => {
                    dismissedResearchRunsRef.current.delete(activeResearchRunId);
                    openResearchPanel(activeResearchRunId);
                  }}
                >
                  <IoGlobeOutline size={16} />
                  <span>Web Research</span>
                </button>
              )}
              {/* Export menu - shown when there are messages */}
              {messages.length > 0 && (
                <AtlasExportMenu
                  onExport={(prompt) => {
                    inputAreaRef.current?.setInputAndSubmit(prompt);
                  }}
                  hasBrief={!!activeBriefId}
                  isMobile={isMobile}
                  disabled={isStreaming}
                />
              )}
            </div>
          </div>

          {hasClientContext && chatScope === 'client' ? (
            <div className="atlas-admin-bar" role="status" aria-label="Admin client chat mode">
              <div className="atlas-admin-bar-left">
                <div className="atlas-admin-bar-title">
                  Viewing selected user's {workspaceContext?.isCompanyScope ? 'company workspace' : 'personal workspace'} <span className="atlas-admin-bar-muted">(user {clientUserId})</span>
                </div>
                <div className="atlas-admin-bar-subtitle">
                  <span className="atlas-admin-pill atlas-admin-pill-warn">File library only — chat posts to your own account</span>
                </div>
              </div>
            </div>
          ) : null}
          {billingNotice ? (
            <div className={`atlas-billing-notice is-${billingNotice.notice_kind || 'usage'}`} role="status">
              <span className="atlas-billing-notice-text">{billingNotice.message}</span>
              <button
                className="atlas-billing-notice-close"
                onClick={() => setBillingNotice(null)}
                aria-label="Dismiss usage notice"
              >
                &times;
              </button>
            </div>
          ) : null}
          {error ? (
            <div className="atlas-error">
              <span className="atlas-error-text">{error}</span>
              <button className="atlas-error-close" onClick={() => setError(null)} aria-label="Dismiss error">&times;</button>
            </div>
          ) : null}
          <div className="atlas-messages" ref={messagesContainerRef}>
            {companyConversationConfirmNode}
            {isLoadingConversation ? (
              <div className="atlas-loading-conversation">
                <span className="atlas-spinner" aria-hidden="true" />
                <span className="atlas-loading-text">Loading conversation…</span>
              </div>
            ) : isWelcomeState ? (
              <div className="atlas-welcome-screen">
                <div className="atlas-welcome-branding">
                  <div className="atlas-welcome-icon">
                    <PiCompassRose size={44} />
                  </div>
                  <h1 className="atlas-welcome-title">{welcomeTitle}</h1>
                </div>

                <div className="atlas-welcome-input">
                  {fileUploadError && (
                    <div className="atlas-file-error">
                      <span>{fileUploadError}</span>
                      <button type="button" onClick={() => setFileUploadError(null)}>
                        <IoCloseOutline size={16} />
                      </button>
                    </div>
                  )}
                  {attachedFiles.length > 0 && (
                    <div className="atlas-attached-files">
                      {attachedFiles.map((file) => (
                        <div key={file.id} className="atlas-attached-file">
                          <span className="atlas-file-icon">
                            {file.kind === 'land_evaluation' ? <MdOutlineTerrain size={16} /> : getFileTypeIcon(file.mimeType, file.name)}
                          </span>
                          <span className="atlas-file-name" title={file.name}>
                            {file.name}
                          </span>
                          <span className="atlas-file-size">{file.kind === 'land_evaluation' ? 'Land Evaluation' : formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            className="atlas-file-remove"
                            onClick={() => handleRemoveFile(file.id)}
                            title="Remove"
                            disabled={isStreaming}
                          >
                            <IoCloseOutline size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <AtlasInputArea
                    ref={inputAreaRef}
                    onSend={handleSend}
                    onStop={handleStop}
                        onQueue={handleQueue}
                    onFileChange={handleFileUpload}
                    onTranscribe={handleTranscribe}
                    isStreaming={isStreaming}
                    isReadOnly={isClientReadOnly}
                    isMobile={isMobile}
                    onScrollToBottom={scrollToBottom}
                    placeholder="Ask Atlas…"
                    sendLabel="Send"
                  />
                </div>

                <AtlasNewChatMenu
                  onSelectPrompt={handleMenuPromptSelect}
                  isCompanyMode={workspaceContext?.isCompanyScope}
                  companyName={workspaceContext?.companyName}
                />
              </div>
	            ) : null}
	            {hasMoreMessages && (
	              <div ref={loadMoreSentinelRef} className="atlas-load-more-sentinel">
	                <span className="atlas-spinner atlas-spinner-small" aria-hidden="true" />
	                <span className="atlas-loading-text">Loading older messages…</span>
	              </div>
	            )}
	            <AtlasMessagesList
	              messages={messages}
	              currentAssistantId={currentAssistantIdRef.current}
	              lastAssistantMsgRef={lastAssistantMsgRef}
	              activity={activity}
	              reasoningTrace={SHOW_REASONING_TRACE ? reasoningTrace : ''}
	              queuedDrafts={queuedDrafts.filter((d) => d.cid === conversationId)}
	              onRemoveQueuedDraft={handleRemoveQueuedDraft}
	              onSendQueuedNow={handleSendQueuedNow}
	              activityKey={activityKey}
	              activityNowTs={activityNowTs}
	              openBriefById={openBriefByIdExclusive}
	              downloadUploadedFile={downloadUploadedFileScoped}
	              downloadSavedFile={downloadSavedFileScoped}
                  previewSavedFile={previewSavedFileScoped}
                  openDocument={openDocument}
	              copiedMessageId={copiedMessageId}
	              onCopyMessage={handleCopyMessage}
	              VizEmbed={VizEmbed}
	              ImageEmbed={ImageEmbed}
	              onNavigateConversation={setConversationId}
	              user={user}
	              conversationId={conversationId}
	              onRollbackMessage={handleRollbackMessage}
	              onLoadToolTraceDetails={handleLoadToolTraceDetails}
	            />
	          </div>

          {adminViewOnly && !isWelcomeState ? (
            <div className="atlas-input-wrapper" ref={composerRef}>
              <div className="atlas-admin-readonly-banner">
                👁 Viewing this user's conversation as admin — read-only
              </div>
            </div>
          ) : null}
          {!briefFullscreen && !showMobileBriefFullscreen && !isWelcomeState && !adminViewOnly ? (
            <div className="atlas-input-wrapper" ref={composerRef}>
              {showFinishedNotice ? (
                <button
                  type="button"
                  className="atlas-finished-notice"
                  onClick={scrollToLastAssistant}
                >
                  <span className="atlas-finished-notice-icon">✓</span>
                  <span className="atlas-finished-notice-text">Atlas has finished</span>
                  <span className="atlas-finished-notice-action">Click to view response</span>
                </button>
              ) : null}

              {/* File upload error */}
              {fileUploadError && (
                <div className="atlas-file-error">
                  <span>{fileUploadError}</span>
                  <button type="button" onClick={() => setFileUploadError(null)}>
                    <IoCloseOutline size={16} />
                  </button>
                </div>
              )}

              {/* Attached files display - files are stored pending until message is sent */}
              {attachedFiles.length > 0 && (
                <div className="atlas-attached-files">
                  {attachedFiles.map((file) => (
                    <div key={file.id} className="atlas-attached-file">
                      <span className="atlas-file-icon">
                        {file.kind === 'land_evaluation' ? <MdOutlineTerrain size={16} /> : getFileTypeIcon(file.mimeType, file.name)}
                      </span>
                      <span className="atlas-file-name" title={file.name}>
                        {file.name}
                      </span>
                      <span className="atlas-file-size">{file.kind === 'land_evaluation' ? 'Land Evaluation' : formatFileSize(file.size)}</span>
                      <button
                        type="button"
                        className="atlas-file-remove"
                        onClick={() => handleRemoveFile(file.id)}
                        title="Remove"
                        disabled={isStreaming}
                      >
                        <IoCloseOutline size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {steerPending && (
                <div className="atlas-steer-pending" role="status">
                  <span className="atlas-steer-pending-dot" aria-hidden="true" />
                  Stopping current response…
                </div>
              )}
              <AtlasInputArea
                ref={inputAreaRef}
                onSend={handleSend}
                onStop={handleStop}
                onQueue={handleQueue}
                onFileChange={handleFileUpload}
                onTranscribe={handleTranscribe}
                isStreaming={isStreaming}
                isReadOnly={isClientReadOnly}
                isMobile={isMobile}
                onScrollToBottom={scrollToBottom}
                placeholder="Ask Atlas…"
                sendLabel="Send"
              />
            </div>
          ) : null}
        </div>
        )}

        {/* Desktop: show divider when a side panel (brief, research or document) is open */}
        {(briefOpen || researchOpen || docOpen) && !briefFullscreen && !(researchOpen && researchFullscreen) && !(docOpen && docFullscreen) && !isMobile ? (
          <div className="atlas-split-divider" onMouseDown={handleDividerMouseDown} role="separator" aria-label="Resize side panel" />
        ) : null}

        {/* Brief Panel - on mobile shows as fullscreen with back button */}
        {(briefOpen || showMobileBriefFullscreen) ? (
          <div 
            className={`atlas-brief ${showMobileBriefFullscreen ? 'atlas-brief-mobile-fullscreen' : ''}`} 
            style={briefFullscreen || showMobileBriefFullscreen ? undefined : { 
              width: `${briefWidth}px`,
              maxWidth: `${BRIEF_MAX_WIDTH}px` 
            }}
          >
            {/* Mobile back header */}
            {showMobileBriefFullscreen && (
              <div className="atlas-mobile-brief-header">
                <button
                  type="button"
                  className="atlas-mobile-back-btn"
                  onClick={handleBackFromBrief}
                  aria-label="Back to chat"
                >
                  <IoArrowBack size={24} />
                </button>
                <span className="atlas-mobile-brief-title">Brief</span>
                <button
                  type="button"
                  className="atlas-mobile-share-btn"
                  onClick={handleBriefPublish}
                  disabled={!activeBriefId || briefPublishing}
                  aria-label="Share brief"
                >
                  <IoShareOutline size={22} />
                </button>
              </div>
            )}
            <AtlasBriefPanel
              brief={activeBrief}
              isOpen={briefOpen}
              isFullscreen={briefFullscreen || showMobileBriefFullscreen}
              mode="private"
              isPublishing={briefPublishing}
              publishError={briefPublishError}
              shareUrl={briefShareUrl}
              versions={briefVersions}
              currentVersionNumber={currentVersionNumber}
              onClose={isMobile ? handleBackFromBrief : handleBriefClose}
              onToggleFullscreen={isMobile ? undefined : handleBriefToggleFullscreen}
              onPublish={handleBriefPublish}
              onSelectVersion={handleSelectVersion}
              fetchViz={fetchVizScoped}
              fetchImage={fetchImageScoped}
              fetchXlsxRange={fetchXlsxRangeScoped}
              downloadXlsx={downloadXlsxScoped}
              isMobile={isMobile}
            />
          </div>
        ) : null}

        {/* Live Web Research panel - shares the brief's side-panel slot */}
        {researchOpen && activeResearchRunId ? (
          <div
            className={`atlas-brief ${isMobile ? 'atlas-brief-mobile-fullscreen' : ''}`}
            style={researchFullscreen || isMobile ? undefined : { width: `${briefWidth}px`, maxWidth: `${BRIEF_MAX_WIDTH}px` }}
          >
            <LiveResearchView
              key={activeResearchRunId}
              runId={activeResearchRunId}
              variant="panel"
              onClose={closeResearchPanel}
              isMobile={isMobile}
              isFullscreen={researchFullscreen}
              onToggleFullscreen={isMobile ? undefined : () => setResearchFullscreen((value) => !value)}
              runTabs={researchRuns}
              onSelectRun={(runId) => {
                dismissedResearchRunsRef.current.delete(runId);
                setActiveResearchRunId(runId);
              }}
              onRunUpdate={handleResearchRunUpdate}
            />
          </div>
        ) : null}

        {/* Document viewer - shares the brief's side-panel slot */}
        {docOpen && docTabs.length ? (
          <div
            className={`atlas-brief ${isMobile ? 'atlas-brief-mobile-fullscreen' : ''}`}
            style={docFullscreen || isMobile ? undefined : { width: `${briefWidth}px`, maxWidth: `${BRIEF_MAX_WIDTH}px` }}
          >
            <AtlasDocumentPanel
              documents={docTabs}
              activeId={activeDocId}
              onSelect={setActiveDocId}
              onCloseTab={closeDocTab}
              onClose={closeDocumentPanel}
              isFullscreen={docFullscreen}
              onToggleFullscreen={isMobile ? undefined : () => setDocFullscreen((value) => !value)}
              isMobile={isMobile}
              loadFile={loadDocumentFile}
              onDownload={downloadDocumentFile}
            />
          </div>
        ) : null}

      </div>
      {introModalNode && typeof document !== 'undefined' ? createPortal(introModalNode, document.body) : null}
      {usageLimitModalNode && typeof document !== 'undefined' ? createPortal(usageLimitModalNode, document.body) : null}
      {snapshotModalNode && typeof document !== 'undefined' ? createPortal(snapshotModalNode, document.body) : null}
      {runtimeDebugModalNode && typeof document !== 'undefined' ? createPortal(runtimeDebugModalNode, document.body) : null}
    </div>
    </CardDecisionContext.Provider>
  );
}
